import { useState, useEffect, createContext, useContext, useCallback, useRef, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, useParams, useSearchParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  ShoppingBag, Heart, User, Search, Menu, X, ChevronRight, ChevronLeft,
  Star, Plus, Minus, Trash2, Package, Truck, CreditCard, CheckCircle,
  Home, Grid3X3, Settings, Users, BarChart3, LogOut, Edit, Eye,
  MessageCircle, Send, RotateCcw, GitCompare, Tag, Lock, Upload, FileSpreadsheet,
  AlertCircle, Check, Loader2, Percent, RefreshCw, Database, Zap, Navigation,
  ShieldCheck, Clock, TrendingUp, DollarSign, Activity, Layers, Mail, Info, XCircle, Key, Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { AdminImportManagerPage } from "@/components/Admin/ImportManagerPage";
import { AdminSEOAuditPage } from "@/components/Admin/AdminSEOAuditPage";
import { ProfitabilityManager } from "@/components/Admin/ProfitabilityManager";
import { ProductDataManager } from "@/components/Admin/ProductDataManager";
import { TadImportManager } from "@/components/Admin/TadImportManager";
import { DatabaseMigration } from "@/components/Admin/DatabaseMigration";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const upsertMetaTag = (selector, attributes, content) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value));
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content || "");
};

const upsertLinkTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("link");
    document.head.appendChild(tag);
  }
  Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value));
};

const upsertJsonLdScript = (scriptId, data) => {
  let scriptTag = document.getElementById(scriptId);
  if (!scriptTag) {
    scriptTag = document.createElement("script");
    scriptTag.id = scriptId;
    scriptTag.type = "application/ld+json";
    document.head.appendChild(scriptTag);
  }
  scriptTag.textContent = JSON.stringify(data || {});
};

const stripHtml = (html) => {
  if (!html) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
};

const applySeoTags = ({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
}) => {
  document.title = title;
  upsertMetaTag('meta[name="description"]', { name: "description" }, description);
  upsertMetaTag('meta[property="og:title"]', { property: "og:title" }, title);
  upsertMetaTag('meta[property="og:description"]', { property: "og:description" }, description);
  upsertMetaTag('meta[property="og:type"]', { property: "og:type" }, type);
  if (image) {
    upsertMetaTag('meta[property="og:image"]', { property: "og:image" }, image);
  }
  if (canonical) {
    upsertLinkTag('link[rel="canonical"]', { rel: "canonical", href: canonical });
    upsertMetaTag('meta[property="og:url"]', { property: "og:url" }, canonical);
  }
  if (jsonLd) {
    upsertJsonLdScript("sitewide-json-ld", jsonLd);
  }
};

const SitewideSEOManager = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let seoTitle = "Voco Living | Stylish Furniture for Every Room";
    let seoDescription = "Discover stylish living, dining, bedroom and kids furniture with fast UK delivery and premium quality from Voco Living.";
    let seoType = "website";

    if (path.startsWith("/shop")) {
      seoTitle = "Shop Furniture | Voco Living";
      seoDescription = "Browse furniture collections by room, style and budget. Find modern, practical pieces for every home.";
    } else if (path.startsWith("/collections")) {
      seoTitle = "Furniture Collections | Voco Living";
      seoDescription = "Explore curated furniture collections with coordinated products for living, bedroom, dining and more.";
    } else if (path.startsWith("/collection/")) {
      const collectionName = decodeURIComponent(path.split("/collection/")[1] || "Collection");
      seoTitle = `${collectionName} Collection | Voco Living`;
      seoDescription = `Explore the ${collectionName} collection at Voco Living, crafted for style, function and lasting quality.`;
    } else if (path.startsWith("/category/")) {
      const categoryName = decodeURIComponent(path.split("/category/")[1] || "Category").replace(/-/g, " ");
      seoTitle = `${categoryName} Furniture | Voco Living`;
      seoDescription = `Shop ${categoryName} furniture at Voco Living with practical designs, quality finishes and UK delivery.`;
    } else if (path.startsWith("/product/")) {
      seoTitle = "Product Details | Voco Living";
      seoDescription = "View detailed product information, dimensions, images and delivery options at Voco Living.";
      seoType = "product";
    }

    const canonical = `${window.location.origin}${path}`;
    applySeoTags({
      title: seoTitle,
      description: seoDescription,
      canonical,
      type: seoType,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Voco Living",
        url: window.location.origin,
        potentialAction: {
          "@type": "SearchAction",
          target: `${window.location.origin}/shop?search={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_path: path,
      page_title: seoTitle,
      page_type: seoType,
    });
  }, [location.pathname]);

  return null;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("cart_session") || null);
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      const currentSession = localStorage.getItem("cart_session");
      if (!user && currentSession) params.append("session_id", currentSession);
      const res = await axios.get(`${API}/cart?${params}`);
      setCart(res.data);
    } catch (e) {
      console.error("Error fetching cart:", e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart, user]);

  const addToCart = async (productId, quantity = 1) => {
    try {
      const params = new URLSearchParams();
      const currentSession = localStorage.getItem("cart_session");
      if (!user && currentSession) params.append("session_id", currentSession);
      const res = await axios.post(`${API}/cart/add?${params}`, { product_id: productId, quantity });
      if (res.data.session_id) {
        localStorage.setItem("cart_session", res.data.session_id);
        setSessionId(res.data.session_id);
      }
      await fetchCart();
      toast.success("Added to cart");
    } catch (e) {
      toast.error("Failed to add to cart");
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      const params = new URLSearchParams();
      const currentSession = localStorage.getItem("cart_session");
      if (!user && currentSession) params.append("session_id", currentSession);
      await axios.put(`${API}/cart/update?${params}`, { product_id: productId, quantity });
      await fetchCart();
    } catch (e) {
      toast.error("Failed to update cart");
    }
  };

  const removeFromCart = async (productId) => {
    try {
      const params = new URLSearchParams();
      const currentSession = localStorage.getItem("cart_session");
      if (!user && currentSession) params.append("session_id", currentSession);
      await axios.delete(`${API}/cart/item/${productId}?${params}`);
      await fetchCart();
      toast.success("Removed from cart");
    } catch (e) {
      toast.error("Failed to remove from cart");
    }
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateQuantity, removeFromCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

// Comparison Context
const ComparisonContext = createContext(null);
export const useComparison = () => useContext(ComparisonContext);

const ComparisonProvider = ({ children }) => {
  const [compareItems, setCompareItems] = useState([]);
  const MAX_COMPARE = 4;

  const addToCompare = (product) => {
    if (compareItems.find(p => p.id === product.id)) {
      toast.info("Product already in comparison");
      return;
    }
    if (compareItems.length >= MAX_COMPARE) {
      toast.error(`Maximum ${MAX_COMPARE} products can be compared`);
      return;
    }
    setCompareItems([...compareItems, product]);
    toast.success("Added to comparison");
  };

  const removeFromCompare = (productId) => {
    setCompareItems(compareItems.filter(p => p.id !== productId));
  };

  const clearComparison = () => {
    setCompareItems([]);
  };

  return (
    <ComparisonContext.Provider value={{ compareItems, addToCompare, removeFromCompare, clearComparison }}>
      {children}
    </ComparisonContext.Provider>
  );
};

// Recently Viewed Context - stores in localStorage
const RecentlyViewedContext = createContext(null);
export const useRecentlyViewed = () => useContext(RecentlyViewedContext);

const RecentlyViewedProvider = ({ children }) => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const MAX_RECENT = 8;

  useEffect(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem('recentlyViewed');
    if (stored) {
      try {
        setRecentlyViewed(JSON.parse(stored));
      } catch (e) {
        localStorage.removeItem('recentlyViewed');
      }
    }
  }, []);

  const addToRecentlyViewed = (product) => {
    if (!product || !product.id) return;
    
    setRecentlyViewed(prev => {
      // Remove if already exists
      const filtered = prev.filter(p => p.id !== product.id);
      // Add to front, limit to MAX_RECENT
      const updated = [
        { 
          id: product.id, 
          name: product.name, 
          price: product.price, 
          images: product.images?.slice(0, 1) || [],
          collection_name: product.collection_name,
          rrp_price: product.rrp_price,
          on_sale: product.on_sale
        }, 
        ...filtered
      ].slice(0, MAX_RECENT);
      
      // Save to localStorage
      localStorage.setItem('recentlyViewed', JSON.stringify(updated));
      return updated;
    });
  };

  const clearRecentlyViewed = () => {
    setRecentlyViewed([]);
    localStorage.removeItem('recentlyViewed');
  };

  return (
    <RecentlyViewedContext.Provider value={{ recentlyViewed, addToRecentlyViewed, clearRecentlyViewed }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
};

// Header Component - Furniture Village Style
const Header = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [navigation, setNavigation] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/navigation`).then(res => setNavigation(res.data.items || []));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
    }
  };

  const getNavLink = (item) => {
    if (item.type === "collections") return "/collections";
    if (item.type === "page") return `/customer-service`;
    // Room category pages with dedicated boca-living style
    const roomPages = ["bedroom", "living", "dining", "kids", "home-office", "packages"];
    if (roomPages.includes(item.id)) return `/${item.id}`;
    if (item.filter?.category) return `/category/${item.id}`;
    if (item.filter?.on_sale) return "/sale";
    return "/shop";
  };

  const mainNavItems = navigation.filter(n => n.enabled).slice(0, 8);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm" data-testid="header">
      {/* Top Bar - Trust Badges */}
      <div className="bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="hidden md:flex items-center justify-center gap-8 py-2 text-xs">
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> 1 Year Guarantee
            </span>
            <span className="flex items-center gap-2">
              <Tag className="w-4 h-4" /> Price Match Guaranteed
            </span>
            <span className="flex items-center gap-2">
              <Truck className="w-4 h-4" /> Free Delivery to UK Mainland
            </span>
          </div>
          <div className="md:hidden text-center py-2 text-xs font-medium">
            FREE DELIVERY to UK Mainland | Price Match Guaranteed
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4">
        {/* Main Header Row */}
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 -ml-2" data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6 text-gray-700" />
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo">
            <span className="text-2xl md:text-3xl font-bold text-[#003366] tracking-tight">VOCO</span>
            <span className="text-2xl md:text-3xl font-light text-[#003366] ml-1">Living</span>
          </Link>

          {/* Search Bar - Desktop */}
          <div className="hidden lg:flex flex-1 max-w-xl mx-8">
            <form onSubmit={handleSearch} className="w-full relative">
              <Input 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                placeholder="Search for sofas, beds, dining tables..." 
                className="w-full pl-4 pr-12 py-3 border-2 border-gray-200 rounded-sm focus:border-[#003366] bg-gray-50"
                data-testid="search-input"
              />
              <button type="submit" className="absolute right-0 top-0 bottom-0 px-4 bg-[#003366] text-white rounded-r-sm hover:bg-[#004080] transition-colors" data-testid="search-submit">
                <Search className="w-5 h-5" />
              </button>
            </form>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 md:gap-4">
            {/* Mobile Search */}
            <button onClick={() => setSearchOpen(true)} className="lg:hidden p-2" data-testid="search-btn-mobile">
              <Search className="w-5 h-5 text-gray-700" />
            </button>

            {user ? (
              <>
                <Link to="/wishlist" className="hidden md:flex flex-col items-center p-2 hover:text-[#003366] transition-colors" data-testid="wishlist-btn">
                  <Heart className="w-5 h-5" />
                  <span className="text-xs mt-1">Wishlist</span>
                </Link>
                <Link to="/account" className="hidden md:flex flex-col items-center p-2 hover:text-[#003366] transition-colors" data-testid="account-btn">
                  <User className="w-5 h-5" />
                  <span className="text-xs mt-1">Account</span>
                </Link>
                {user.is_admin && (
                  <Link to="/admin" className="hidden md:block text-sm font-semibold text-[#c41e3a] hover:underline" data-testid="admin-link">Admin</Link>
                )}
              </>
            ) : (
              <Link to="/login" className="hidden md:flex flex-col items-center p-2 hover:text-[#003366] transition-colors" data-testid="login-link">
                <User className="w-5 h-5" />
                <span className="text-xs mt-1">Sign In</span>
              </Link>
            )}

            <Sheet>
              <SheetTrigger asChild>
                <button className="relative flex flex-col items-center p-2 hover:text-[#003366] transition-colors" data-testid="cart-btn">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="hidden md:block text-xs mt-1">Basket</span>
                  {cart.item_count > 0 && (
                    <span className="absolute -top-1 right-0 md:right-1 w-5 h-5 bg-[#c41e3a] text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {cart.item_count}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md">
                <SheetHeader>
                  <SheetTitle className="text-xl font-bold">Your Basket</SheetTitle>
                </SheetHeader>
                <CartDrawer />
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Desktop Navigation Row */}
        <nav className="hidden lg:flex items-center justify-center border-t border-gray-100" data-testid="main-nav">
          {mainNavItems.map((item) => (
            <Link 
              key={item.id}
              to={getNavLink(item)}
              className={`px-5 py-4 text-sm font-semibold uppercase tracking-wide transition-colors border-b-2 border-transparent hover:border-[#003366] ${
                item.id === 'on-sale' ? 'text-[#c41e3a] hover:border-[#c41e3a]' : 'text-gray-700 hover:text-[#003366]'
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Search Dialog - Mobile */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold">Search Products</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              placeholder="Search furniture..." 
              className="flex-1"
            />
            <Button type="submit" className="bg-[#003366] hover:bg-[#004080]">Search</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mobile Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-80 p-0">
          <div className="bg-[#003366] text-white p-4">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-xl font-bold">VOCO Living</Link>
          </div>
          <nav className="flex flex-col">
            {mainNavItems.map((item) => (
              <Link 
                key={item.id}
                to={getNavLink(item)}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-4 text-base font-semibold border-b border-gray-100 ${
                  item.id === 'on-sale' ? 'text-[#c41e3a] bg-red-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="bg-gray-100 px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Account</div>
            {user ? (
              <>
                <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="px-4 py-4 text-base font-semibold text-gray-700 hover:bg-gray-50 border-b border-gray-100">My Account</Link>
                <Link to="/wishlist" onClick={() => setMobileMenuOpen(false)} className="px-4 py-4 text-base font-semibold text-gray-700 hover:bg-gray-50 border-b border-gray-100">Wishlist</Link>
                <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="px-4 py-4 text-base font-semibold text-left text-[#c41e3a] hover:bg-red-50 border-b border-gray-100">Sign Out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="px-4 py-4 text-base font-semibold text-[#003366] hover:bg-gray-50 border-b border-gray-100">Sign In / Register</Link>
            )}
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
};

// Cart Drawer - Furniture Village Style
const CartDrawer = () => {
  const { cart, updateQuantity, removeFromCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <ShoppingBag className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500 font-medium mb-2">Your basket is empty</p>
        <p className="text-gray-400 text-sm mb-4">Start shopping to add items</p>
        <Link to="/shop" className="text-[#003366] font-semibold hover:underline">Continue Shopping</Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 -mx-6 px-6">
        <div className="space-y-4 py-4">
          {cart.items.map((item) => (
            <div key={item.product_id} className="flex gap-4 pb-4 border-b border-gray-100" data-testid={`cart-item-${item.product_id}`}>
              <div className="w-24 h-24 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                {item.product?.images?.[0] && (
                  <img src={item.product.images[0]} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${item.product_id}`} className="font-semibold text-sm text-gray-900 line-clamp-2 hover:text-[#003366]">
                  {item.product?.name}
                </Link>
                <p className="text-[#003366] font-bold mt-1">{item.product?.price}</p>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center border border-gray-200 rounded">
                    <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="p-1.5 hover:bg-gray-100 transition-colors">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="text-sm w-8 text-center font-medium">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="p-1.5 hover:bg-gray-100 transition-colors">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <button onClick={() => removeFromCart(item.product_id)} className="text-gray-400 hover:text-[#c41e3a] transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
      
      {/* Cart Summary */}
      <div className="border-t border-gray-200 pt-4 mt-4 space-y-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Subtotal ({cart.item_count} items)</span>
          <span className="font-bold text-lg">£{cart.total.toFixed(2)}</span>
        </div>
        <p className="text-xs text-gray-500">Delivery calculated at checkout</p>
        <Button 
          className="w-full bg-[#003366] hover:bg-[#004080] text-white font-bold py-3" 
          onClick={() => navigate(user ? "/checkout" : "/login?redirect=/checkout")}
          data-testid="checkout-btn"
        >
          {user ? "Checkout Securely" : "Sign in to Checkout"}
        </Button>
        <Link to="/shop" className="block text-center text-[#003366] text-sm font-semibold hover:underline">
          Continue Shopping
        </Link>
      </div>
    </div>
  );
};

// Product Card - Furniture Village Style with Sale Badge
const ProductCard = ({ product, onAddToWishlist, onQuickView }) => {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { addToCompare, compareItems } = useComparison();
  const isInCompare = compareItems.find(p => p.id === product.id);

  const calculateDiscount = () => {
    if (product.rrp_price && product.price_numeric) {
      const rrp = parseFloat(product.rrp_price.replace(/[^0-9.]/g, ''));
      if (rrp > product.price_numeric) {
        return Math.round((1 - product.price_numeric / rrp) * 100);
      }
    }
    return 0;
  };

  const discount = calculateDiscount();

  return (
    <div className="product-card group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300" data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative aspect-square bg-gray-100 overflow-hidden">
          {product.images?.[0] ? (
            <img 
              src={product.images[0]} 
              alt={product.name} 
              className="w-full h-full object-cover product-image"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-12 h-12 text-gray-300" />
            </div>
          )}
          
          {/* Sale Badge */}
          {product.on_sale && discount > 0 && (
            <div className="absolute top-3 left-3 flex flex-col gap-1">
              <span className="bg-[#c41e3a] text-white px-2 py-1 text-xs font-bold">
                {discount}% OFF
              </span>
            </div>
          )}

          {/* Quick Delivery Badge */}
          {product.quick_delivery && (
            <span className="absolute top-3 right-3 bg-green-600 text-white px-2 py-1 text-xs font-medium">
              Quick Delivery
            </span>
          )}

          {/* Image Count Badge */}
          {product.images?.length > 1 && (
            <span className="absolute bottom-3 right-3 bg-black/60 text-white px-2 py-1 text-xs rounded">
              +{product.images.length - 1}
            </span>
          )}

          {/* Quick View & Compare Overlay */}
          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex gap-2">
              {onQuickView && (
                <button 
                  onClick={(e) => { e.preventDefault(); onQuickView(product); }}
                  className="flex-1 bg-white text-gray-900 py-2 text-sm font-semibold hover:bg-gray-100 transition-colors"
                  data-testid={`quick-view-${product.id}`}
                >
                  Quick View
                </button>
              )}
              <button 
                onClick={(e) => { e.preventDefault(); addToCompare(product); }}
                className={`p-2 ${isInCompare ? 'bg-[#003366] text-white' : 'bg-white text-gray-900 hover:bg-gray-100'} transition-colors`}
                title="Compare"
              >
                <GitCompare className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-4">
          {/* Collection */}
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{product.collection_name}</p>
          
          {/* Product Name */}
          <h3 className="font-semibold text-gray-900 text-sm md:text-base line-clamp-2 min-h-[40px] group-hover:text-[#003366] transition-colors">{product.name}</h3>
          
          {/* Pricing - Was/Now Style */}
          <div className="mt-2">
            {product.rrp_price && product.rrp_price !== product.price ? (
              <div className="flex flex-col">
                <span className="text-gray-500 text-xs line-through">Was {product.rrp_price}</span>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[#c41e3a] font-bold text-lg">{product.price}</span>
                  {discount > 0 && (
                    <span className="bg-[#c41e3a] text-white text-xs px-1.5 py-0.5 font-semibold">
                      SAVE {discount}%
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-gray-900 font-bold text-lg">{product.price}</span>
            )}
          </div>

          {/* Category */}
          {product.category && (
            <p className="text-xs text-gray-500 mt-2">{product.category}</p>
          )}
        </div>
      </Link>

      {/* Add to Cart Button */}
      <div className="px-4 pb-4">
        <Button 
          size="sm" 
          className="w-full bg-[#003366] hover:bg-[#004080] text-white font-semibold py-2"
          onClick={(e) => { e.preventDefault(); addToCart(product.id); }}
          data-testid={`add-to-cart-${product.id}`}
        >
          Add to Basket
        </Button>
      </div>
    </div>
  );
};

// Quick View Modal
const QuickViewModal = ({ product, open, onClose }) => {
  const { addToCart } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <div className="grid md:grid-cols-2">
          {/* Images */}
          <div className="bg-warm-subtle p-4">
            <div className="aspect-square overflow-hidden rounded-lg mb-4">
              {product.images?.[selectedImage] && (
                <img 
                  src={product.images[selectedImage]} 
                  alt={product.name} 
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {product.images.slice(0, 4).map((img, i) => (
                  <button 
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-16 h-16 flex-shrink-0 rounded overflow-hidden ${i === selectedImage ? 'ring-2 ring-warm-dark' : 'opacity-60'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="p-6">
            <DialogHeader>
              <p className="text-xs uppercase tracking-wider text-warm-muted">{product.collection_name}</p>
              <DialogTitle className="font-serif text-2xl">{product.name}</DialogTitle>
            </DialogHeader>
            
            <div className="flex items-center gap-3 my-4">
              <span className="font-serif text-2xl text-warm-dark">{product.price}</span>
              {product.rrp_price && product.rrp_price !== product.price && (
                <>
                  <span className="text-lg text-warm-muted line-through">{product.rrp_price}</span>
                  <Badge className="bg-red-100 text-red-700">SALE</Badge>
                </>
              )}
            </div>

            {product.description && (
              <div 
                className="text-warm-muted text-sm mb-4 line-clamp-3 [&>p]:mb-0"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            )}

            {product.category && (
              <Badge variant="secondary" className="mb-4">{product.category}</Badge>
            )}

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border border-warm rounded-full">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="p-2">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <Button 
                className="flex-1 btn-primary"
                onClick={() => { addToCart(product.id, quantity); onClose(); }}
              >
                Add to Cart
              </Button>
            </div>

            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => { onClose(); navigate(`/product/${product.id}`); }}
            >
              View Full Details
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Review Section Component
const ReviewSection = ({ productId, reviews: initialReviews }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState(initialReviews);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const hasReviewed = user && reviews.some(r => r.user_id === user.id);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to write a review");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reviews`, {
        product_id: productId,
        rating,
        title,
        comment
      });
      setReviews([res.data.review, ...reviews]);
      setShowForm(false);
      setTitle("");
      setComment("");
      setRating(5);
      toast.success("Review submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    }
    setLoading(false);
  };

  return (
    <div className="mt-20" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-2xl">Customer Reviews ({reviews.length})</h2>
        {user && !hasReviewed && (
          <Button onClick={() => setShowForm(!showForm)} variant="outline">
            {showForm ? "Cancel" : "Write a Review"}
          </Button>
        )}
      </div>

      {/* Review Form */}
      {showForm && (
        <form onSubmit={submitReview} className="bg-warm-paper p-6 rounded-lg mb-8" data-testid="review-form">
          <h3 className="font-medium mb-4">Write Your Review</h3>
          
          <div className="mb-4">
            <label className="block text-sm text-warm-muted mb-2">Rating</label>
            <div className="flex gap-1">
              {[1,2,3,4,5].map((star) => (
                <button
                  type="button"
                  key={star}
                  onClick={() => setRating(star)}
                  className="p-1"
                >
                  <Star className={`w-6 h-6 ${star <= rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm text-warm-muted mb-2">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Summarize your experience"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-warm-muted mb-2">Your Review</label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did you like or dislike?"
              rows={4}
              required
            />
          </div>

          <Button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Submitting..." : "Submit Review"}
          </Button>
        </form>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-8 text-warm-muted">
          <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No reviews yet. Be the first to review this product!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="border-b border-warm pb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} />
                  ))}
                </div>
                <span className="font-medium">{review.title}</span>
              </div>
              <p className="text-warm-muted mb-2">{review.comment}</p>
              <p className="text-sm text-warm-muted">{review.user_name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Homepage - Furniture Village Style
const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [saleProducts, setSaleProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [heroSlide, setHeroSlide] = useState(0);

  useEffect(() => {
    axios.get(`${API}/products/featured?limit=10`).then(res => setFeatured(res.data));
    axios.get(`${API}/products?on_sale=true&limit=10`).then(res => setSaleProducts(res.data.products || []));
    axios.get(`${API}/collections`).then(res => setCollections(res.data.slice(0, 8)));
  }, []);

  const heroSlides = [
    {
      title: "Final Reductions",
      subtitle: "Last chance to save on selected sofas, beds & dining",
      cta: "Shop Sale",
      link: "/sale",
      bg: "bg-gradient-to-r from-[#c41e3a] to-[#8b0000]",
      image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&q=80&w=1200"
    },
    {
      title: "New Season Arrivals",
      subtitle: "Discover our latest collection of stylish furniture",
      cta: "Shop New In",
      link: "/shop",
      bg: "bg-gradient-to-r from-[#003366] to-[#001a33]",
      image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&q=80&w=1200"
    },
    {
      title: "Interest Free Credit",
      subtitle: "Spread the cost with 0% APR available",
      cta: "Learn More",
      link: "/customer-service",
      bg: "bg-gradient-to-r from-[#2e7d32] to-[#1b5e20]",
      image: "https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&q=80&w=1200"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const categoryCards = [
    { name: "Sofas", image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&q=80&w=600", link: "/category/living" },
    { name: "Dining", image: "https://images.unsplash.com/photo-1617806118233-18e1de247200?auto=format&fit=crop&q=80&w=600", link: "/category/dining" },
    { name: "Beds", image: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&q=80&w=600", link: "/category/bedroom" },
    { name: "Quick Delivery", image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&q=80&w=600", link: "/shop?quick_delivery=true" },
  ];

  return (
    <div data-testid="homepage" className="bg-white">
      {/* Hero Banner Carousel */}
      <section className="relative overflow-hidden">
        <div className="relative h-[300px] md:h-[400px] lg:h-[500px]">
          {heroSlides.map((slide, index) => (
            <div 
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ${index === heroSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            >
              <div className={`absolute inset-0 ${slide.bg}`}>
                <img 
                  src={slide.image} 
                  alt={slide.title} 
                  className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-30"
                />
              </div>
              <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center">
                <div className="text-white max-w-xl">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 animate-fade-in-up">
                    {slide.title}
                  </h1>
                  <p className="text-lg md:text-xl mb-6 opacity-90 animate-fade-in-up stagger-1">
                    {slide.subtitle}
                  </p>
                  <Link 
                    to={slide.link} 
                    className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 font-bold hover:bg-gray-100 transition-colors animate-fade-in-up stagger-2"
                    data-testid="hero-cta"
                  >
                    {slide.cta} <ChevronRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Slide Indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setHeroSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${index === heroSlide ? 'bg-white' : 'bg-white/50'}`}
              data-testid={`hero-dot-${index}`}
            />
          ))}
        </div>
      </section>

      {/* Category Cards */}
      <section className="py-8 md:py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categoryCards.map((cat, index) => (
              <Link 
                key={index}
                to={cat.link}
                className="category-card group relative aspect-[4/3] rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow"
                data-testid={`category-card-${cat.name.toLowerCase()}`}
              >
                <img 
                  src={cat.image} 
                  alt={cat.name} 
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                  <h3 className="text-white text-lg md:text-xl font-bold">{cat.name}</h3>
                  <span className="text-white/80 text-sm flex items-center gap-1 mt-1 group-hover:text-white transition-colors">
                    Shop now <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sale Products Carousel */}
      {saleProducts.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="bg-[#c41e3a] text-white px-3 py-1 text-sm font-bold">SALE</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Final Reductions</h2>
                </div>
                <p className="text-gray-600">Last chance to save on selected items</p>
              </div>
              <Link to="/sale" className="hidden md:flex items-center gap-2 text-[#003366] font-semibold hover:underline">
                Shop All Sale <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {saleProducts.slice(0, 5).map((product) => (
                <ProductCard key={product.id} product={product} onQuickView={setQuickViewProduct} />
              ))}
            </div>
            <div className="text-center mt-8 md:hidden">
              <Link to="/sale" className="btn-sale inline-flex items-center gap-2">
                Shop All Sale <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Shop by Room */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Shop by Room</h2>
            <p className="text-gray-600">Find furniture for every space in your home</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/living" className="group relative h-[300px] md:h-[400px] overflow-hidden rounded-lg shadow-lg" data-testid="shop-living">
              <img 
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&q=80&w=800" 
                alt="Living Room" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-white text-2xl font-bold mb-2">Living Room</h3>
                <span className="text-white bg-[#003366] px-4 py-2 font-semibold inline-flex items-center gap-2 group-hover:bg-[#004080] transition-colors">
                  Shop Sofas <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
            <Link to="/bedroom" className="group relative h-[300px] md:h-[400px] overflow-hidden rounded-lg shadow-lg" data-testid="shop-bedroom">
              <img 
                src="https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&q=80&w=800" 
                alt="Bedroom" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-white text-2xl font-bold mb-2">Bedroom</h3>
                <span className="text-white bg-[#003366] px-4 py-2 font-semibold inline-flex items-center gap-2 group-hover:bg-[#004080] transition-colors">
                  Shop Beds <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
            <Link to="/dining" className="group relative h-[300px] md:h-[400px] overflow-hidden rounded-lg shadow-lg" data-testid="shop-dining">
              <img 
                src="https://images.unsplash.com/photo-1617806118233-18e1de247200?auto=format&fit=crop&q=80&w=800" 
                alt="Dining Room" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-white text-2xl font-bold mb-2">Dining Room</h3>
                <span className="text-white bg-[#003366] px-4 py-2 font-semibold inline-flex items-center gap-2 group-hover:bg-[#004080] transition-colors">
                  Shop Dining <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Top Picks</h2>
              <p className="text-gray-600">Our most popular products</p>
            </div>
            <Link to="/shop" className="hidden md:flex items-center gap-2 text-[#003366] font-semibold hover:underline">
              View All <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {featured.slice(0, 5).map((product) => (
              <ProductCard key={product.id} product={product} onQuickView={setQuickViewProduct} />
            ))}
          </div>
        </div>
      </section>

      {/* Collections Grid */}
      {collections.length > 0 && (
        <section className="py-12 md:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Browse Collections</h2>
              <p className="text-gray-600">Explore our curated furniture ranges</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {collections.slice(0, 8).map((col) => (
                <Link 
                  key={col.name} 
                  to={`/shop?collection=${encodeURIComponent(col.name)}`}
                  className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                  data-testid={`collection-${col.name}`}
                >
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    {col.image ? (
                      <img src={col.image} alt={col.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 group-hover:text-[#003366] transition-colors">{col.name}</h3>
                    <p className="text-sm text-gray-500">{col.count} products</p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/collections" className="btn-secondary inline-flex items-center gap-2">
                View All Collections <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Recently Viewed Carousel */}
      <RecentlyViewedCarousel />

      {/* Trust Badges Section */}
      <section className="py-12 md:py-16 bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <ShieldCheck className="w-10 h-10 mb-3 opacity-90" />
              <h3 className="font-bold text-lg mb-1">1 Year Guarantee</h3>
              <p className="text-sm opacity-75">On all products</p>
            </div>
            <div className="flex flex-col items-center">
              <Tag className="w-10 h-10 mb-3 opacity-90" />
              <h3 className="font-bold text-lg mb-1">Price Match Guaranteed</h3>
              <p className="text-sm opacity-75">On all brands</p>
            </div>
            <div className="flex flex-col items-center">
              <Truck className="w-10 h-10 mb-3 opacity-90" />
              <h3 className="font-bold text-lg mb-1">Free Delivery</h3>
              <p className="text-sm opacity-75">To UK Mainland</p>
            </div>
            <div className="flex flex-col items-center">
              <RotateCcw className="w-10 h-10 mb-3 opacity-90" />
              <h3 className="font-bold text-lg mb-1">Easy Returns</h3>
              <p className="text-sm opacity-75">Hassle-free returns</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick View Modal */}
      <QuickViewModal 
        product={quickViewProduct} 
        open={!!quickViewProduct} 
        onClose={() => setQuickViewProduct(null)} 
      />
    </div>
  );
};

// Recently Viewed Carousel Component
const RecentlyViewedCarousel = () => {
  const { recentlyViewed } = useRecentlyViewed();
  const scrollRef = useRef(null);

  if (!recentlyViewed || recentlyViewed.length === 0) return null;

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 280;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const calculateDiscount = (product) => {
    if (product.rrp_price && product.price) {
      const rrp = parseFloat(product.rrp_price.replace(/[^0-9.]/g, ''));
      const current = parseFloat(product.price.replace(/[^0-9.]/g, ''));
      if (rrp > current) {
        return Math.round((1 - current / rrp) * 100);
      }
    }
    return 0;
  };

  return (
    <section className="py-12 md:py-16 bg-gray-50" data-testid="recently-viewed-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              <Clock className="w-6 h-6 inline-block mr-2 text-[#003366]" />
              Recently Viewed
            </h2>
            <p className="text-gray-600">Continue where you left off</p>
          </div>
          <div className="hidden md:flex gap-2">
            <button 
              onClick={() => scroll('left')} 
              className="carousel-nav-btn static translate-y-0"
              data-testid="recent-scroll-left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={() => scroll('right')} 
              className="carousel-nav-btn static translate-y-0"
              data-testid="recent-scroll-right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {recentlyViewed.map((product) => {
            const discount = calculateDiscount(product);
            return (
              <Link 
                key={product.id}
                to={`/product/${product.id}`}
                className="flex-shrink-0 w-[200px] md:w-[220px] bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                style={{ scrollSnapAlign: 'start' }}
                data-testid={`recent-product-${product.id}`}
              >
                <div className="relative aspect-square bg-gray-100 overflow-hidden">
                  {product.images?.[0] ? (
                    <img 
                      src={product.images[0]} 
                      alt={product.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-10 h-10 text-gray-300" />
                    </div>
                  )}
                  {product.on_sale && discount > 0 && (
                    <span className="absolute top-2 left-2 bg-[#c41e3a] text-white px-2 py-1 text-xs font-bold">
                      {discount}% OFF
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{product.collection_name}</p>
                  <h3 className="font-semibold text-sm text-gray-900 line-clamp-2 group-hover:text-[#003366] transition-colors min-h-[40px]">
                    {product.name}
                  </h3>
                  <div className="mt-2">
                    {product.rrp_price && product.rrp_price !== product.price ? (
                      <div className="flex items-center gap-2">
                        <span className="text-[#c41e3a] font-bold">{product.price}</span>
                        <span className="text-gray-400 text-xs line-through">{product.rrp_price}</span>
                      </div>
                    ) : (
                      <span className="text-gray-900 font-bold">{product.price}</span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Shop Page - Furniture Village Style
const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  const collection = searchParams.get("collection") || "";
  const category = searchParams.get("category") || "";
  const search = searchParams.get("search") || "";
  const sort = searchParams.get("sort") || "name";
  const page = parseInt(searchParams.get("page") || "1");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (collection) params.append("collection", collection);
    if (category) params.append("category", category);
    if (search) params.append("search", search);
    params.append("sort", sort);
    params.append("page", page.toString());
    params.append("limit", "16");

    axios.get(`${API}/products?${params}`).then(res => {
      setProducts(res.data.products);
      setCollections(res.data.collections);
      setCategories(res.data.categories || []);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setLoading(false);
    });
  }, [collection, category, search, sort, page]);

  const updateParams = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    if (key !== "page") newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const hasActiveFilters = collection || category || search;

  return (
    <div className="bg-white min-h-screen" data-testid="shop-page">
      {/* Page Header */}
      <div className="bg-gray-50 py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#003366]">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">Shop</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Shop</h1>
          {search && (
            <p className="text-gray-600 mt-2">Search results for "{search}"</p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-lg text-gray-900">Filters</h2>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-sm text-[#c41e3a] hover:underline font-medium">
                    Clear all
                  </button>
                )}
              </div>
              
              {search && (
                <div className="pb-4 border-b border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">Searching for:</p>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#003366] text-white">{search}</Badge>
                    <button onClick={() => updateParams("search", "")} className="text-gray-400 hover:text-[#c41e3a]">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Category Filter */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">Category</label>
                  <Select value={category || "all"} onValueChange={(v) => updateParams("category", v === "all" ? "" : v)}>
                    <SelectTrigger className="w-full border-gray-200" data-testid="category-filter">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Collection Filter */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">Collection</label>
                  <Select value={collection || "all"} onValueChange={(v) => updateParams("collection", v === "all" ? "" : v)}>
                    <SelectTrigger className="w-full border-gray-200" data-testid="collection-filter">
                      <SelectValue placeholder="All Collections" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Collections</SelectItem>
                      {collections.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Sort */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">Sort By</label>
                  <Select value={sort} onValueChange={(v) => updateParams("sort", v)}>
                    <SelectTrigger className="w-full border-gray-200" data-testid="sort-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name A-Z</SelectItem>
                      <SelectItem value="price_asc">Price: Low to High</SelectItem>
                      <SelectItem value="price_desc">Price: High to Low</SelectItem>
                      <SelectItem value="newest">Newest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Tags */}
              {hasActiveFilters && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Active Filters:</p>
                  <div className="flex flex-wrap gap-2">
                    {category && (
                      <Badge variant="secondary" className="bg-gray-100 text-gray-700 flex items-center gap-1 px-3 py-1">
                        {category}
                        <button onClick={() => updateParams("category", "")} className="ml-1 hover:text-[#c41e3a]">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    )}
                    {collection && (
                      <Badge variant="secondary" className="bg-gray-100 text-gray-700 flex items-center gap-1 px-3 py-1">
                        {collection}
                        <button onClick={() => updateParams("collection", "")} className="ml-1 hover:text-[#c41e3a]">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Results Count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">{total} products</p>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-square bg-gray-200 rounded-lg mb-4" />
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your filters or search terms</p>
                <Button onClick={clearFilters} variant="outline" className="border-[#003366] text-[#003366]">
                  Clear all filters
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} onQuickView={setQuickViewProduct} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => updateParams("page", String(page - 1))}
                      className="border-gray-300"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const pageNum = i + 1;
                      return (
                        <Button 
                          key={pageNum}
                          variant={pageNum === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => updateParams("page", String(pageNum))}
                          className={pageNum === page ? "bg-[#003366] text-white" : "border-gray-300"}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    {totalPages > 5 && <span className="px-2 text-gray-400">...</span>}
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => updateParams("page", String(page + 1))}
                      className="border-gray-300"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quick View Modal */}
      <QuickViewModal 
        product={quickViewProduct} 
        open={!!quickViewProduct} 
        onClose={() => setQuickViewProduct(null)} 
      />
    </div>
  );
};

// Sale Page - Furniture Village Style
const SalePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("price_asc");
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products?on_sale=true&sort=${sort}&page=${page}&limit=16`)
      .then(res => {
        setProducts(res.data.products || []);
        setTotal(res.data.total);
        setTotalPages(res.data.total_pages);
        setLoading(false);
      });
  }, [sort, page]);

  return (
    <div className="bg-white min-h-screen" data-testid="sale-page">
      {/* Sale Hero Banner */}
      <div className="bg-gradient-to-r from-[#c41e3a] to-[#8b0000] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 text-center text-white">
          <span className="inline-block bg-white text-[#c41e3a] px-4 py-2 font-bold text-sm mb-4">SALE</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-3">Special Offers</h1>
          <p className="text-white/90 text-lg">{total} items on sale</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Sort Control */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">{total} products</p>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-48 border-gray-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
              <SelectItem value="name">Name A-Z</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square bg-gray-200 rounded-lg mb-4" />
                <div className="h-4 bg-gray-200 rounded mb-2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} onQuickView={setQuickViewProduct} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-12">
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="border-gray-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-4 flex items-center text-sm text-gray-600">Page {page} of {totalPages}</span>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="border-gray-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick View Modal */}
      <QuickViewModal 
        product={quickViewProduct} 
        open={!!quickViewProduct} 
        onClose={() => setQuickViewProduct(null)} 
      />
    </div>
  );
};

// Product Detail Page - Furniture Village Style
const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { addToCompare, compareItems } = useComparison();
  const { addToRecentlyViewed } = useRecentlyViewed();

  // Fetch product data
  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(res => {
      setProduct(res.data);
    });
    setSelectedImage(0);
  }, [id]);

  // Add to recently viewed - separate effect to avoid re-render issues
  useEffect(() => {
    if (product) {
      addToRecentlyViewed(product);
    }
  }, [product?.id]); // Only run when product ID changes, not on every render

  useEffect(() => {
    if (!product) return;

    const gtm = product.gtm || {};
    const cleanDescription = (stripHtml(product.body_html) || stripHtml(product.description) || `${product.name} from Voco Living`).slice(0, 300);
    const pageTitle = gtm.page_title || `${product.name} | Voco Living`;
    const canonical = gtm.page_path || `${window.location.origin}/product/${product.id}`;
    const productImage = product.images?.[0] || null;
    const skuValue = gtm?.item?.item_sku || gtm?.item?.magento_sku || product.sku || product.id;
    const offerPrice = Number(product.price_numeric || gtm?.item?.price || gtm?.value || 0);

    const productJsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: gtm?.item?.item_name || product.name,
      sku: skuValue,
      category: gtm?.item?.item_category || product.category || product.collection_name,
      image: Array.isArray(product.images) ? product.images : [],
      description: cleanDescription,
      brand: {
        "@type": "Brand",
        name: "Voco Living",
      },
      offers: {
        "@type": "Offer",
        priceCurrency: gtm.currency || "GBP",
        price: offerPrice || undefined,
        availability: product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        url: canonical,
      },
    };

    applySeoTags({
      title: pageTitle,
      description: cleanDescription,
      canonical,
      image: productImage,
      type: "product",
      jsonLd: productJsonLd,
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: gtm.event || "view_item",
      ecommerce: {
        currency: gtm.currency || "GBP",
        value: gtm.value || offerPrice || 0,
        items: [
          {
            item_id: gtm?.item?.item_id || product.id,
            item_sku: gtm?.item?.item_sku || product.sku,
            item_name: gtm?.item?.item_name || product.name,
            item_list_id: gtm?.item?.item_list_id || product.collection_name,
            item_list_name: gtm?.item?.item_list_name || product.collection_name,
            item_category: gtm?.item?.item_category || product.category,
            item_category2: gtm?.item?.item_category2 || undefined,
            item_category3: gtm?.item?.item_category3 || undefined,
            item_category4: gtm?.item?.item_category4 || undefined,
            price: gtm?.item?.price || offerPrice || 0,
          },
        ],
      },
      page_type: gtm.page_type || "product",
      page_path: gtm.page_path || canonical,
      page_title: pageTitle,
      virtual_page: gtm.virtual_page || `/product/${product.id}`,
      breadcrumb: Array.isArray(gtm.breadcrumb) && gtm.breadcrumb.length > 0
        ? gtm.breadcrumb
        : [product.collection_name, product.name].filter(Boolean),
    });
  }, [product]);

  const addToWishlist = async () => {
    if (!user) {
      toast.error("Please sign in to add to wishlist");
      return;
    }
    try {
      await axios.post(`${API}/wishlist/add/${id}`);
      toast.success("Added to wishlist");
    } catch (e) {
      toast.error("Failed to add to wishlist");
    }
  };

  const calculateDiscount = () => {
    if (product?.rrp_price && product?.price_numeric) {
      const rrp = parseFloat(product.rrp_price.replace(/[^0-9.]/g, ''));
      if (rrp > product.price_numeric) {
        return Math.round((1 - product.price_numeric / rrp) * 100);
      }
    }
    return 0;
  };

  // Remove duplicate images - memoized to avoid recalculation
  const uniqueImages = useMemo(() => {
    if (!product?.images) return [];
    const seen = new Set();
    return product.images.filter(img => {
      // Normalize URL for comparison (remove query params, trailing slashes)
      const normalizedUrl = img.split('?')[0].replace(/\/$/, '');
      if (seen.has(normalizedUrl)) {
        return false;
      }
      seen.add(normalizedUrl);
      return true;
    });
  }, [product?.images]);

  // Handle image navigation with uniqueImages
  const handleImageSelect = (index) => {
    if (index >= 0 && index < uniqueImages.length) {
      setSelectedImage(index);
    }
  };

  const nextImage = () => {
    if (uniqueImages.length > 1) {
      setSelectedImage((prev) => (prev + 1) % uniqueImages.length);
    }
  };

  const prevImage = () => {
    if (uniqueImages.length > 1) {
      setSelectedImage((prev) => (prev - 1 + uniqueImages.length) % uniqueImages.length);
    }
  };

  // Ensure selectedImage is within bounds
  useEffect(() => {
    if (uniqueImages.length > 0 && selectedImage >= uniqueImages.length) {
      setSelectedImage(0);
    }
  }, [uniqueImages.length, selectedImage]);

  const isInCompare = compareItems.find(p => p.id === product?.id);
  const discount = product ? calculateDiscount() : 0;

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-12">
          <div className="aspect-square bg-gray-200 animate-pulse rounded-lg" />
          <div className="space-y-4">
            <div className="h-6 bg-gray-200 w-1/4 animate-pulse rounded" />
            <div className="h-10 bg-gray-200 w-3/4 animate-pulse rounded" />
            <div className="h-8 bg-gray-200 w-1/3 animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="product-detail">
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4 border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#003366]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/shop" className="hover:text-[#003366]">Shop</Link>
          {product.category && (
            <>
              <ChevronRight className="w-4 h-4" />
              <Link to={`/category/${product.category.toLowerCase().replace(' ', '-')}`} className="hover:text-[#003366]">{product.category}</Link>
            </>
          )}
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 truncate max-w-[200px]">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative aspect-square bg-gray-100 overflow-hidden rounded-lg group">
              {uniqueImages[selectedImage] ? (
                <img 
                  src={uniqueImages[selectedImage]} 
                  alt={product.name} 
                  className="w-full h-full object-cover cursor-zoom-in transition-transform duration-300 group-hover:scale-105"
                  onClick={() => setLightboxOpen(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-16 h-16 text-gray-300" />
                </div>
              )}
              
              {/* Navigation Arrows */}
              {uniqueImages.length > 1 && (
                <>
                  <button 
                    onClick={prevImage}
                    className="carousel-nav-btn left-4"
                    data-testid="prev-image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={nextImage}
                    className="carousel-nav-btn right-4"
                    data-testid="next-image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Sale Badge */}
              {product.on_sale && discount > 0 && (
                <span className="absolute top-4 left-4 bg-[#c41e3a] text-white px-4 py-2 text-sm font-bold">
                  SALE
                </span>
              )}

              {/* Image Counter */}
              {uniqueImages.length > 1 && (
                <span className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 text-sm rounded-full">
                  {selectedImage + 1} / {uniqueImages.length}
                </span>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {uniqueImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide relative z-20" data-testid="image-thumbnails">
                {uniqueImages.map((img, i) => (
                  <div 
                    key={i}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleImageSelect(i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleImageSelect(i);
                      }
                    }}
                    className={`relative w-20 h-20 flex-shrink-0 rounded-md overflow-hidden transition-all cursor-pointer select-none z-30 ${
                      i === selectedImage 
                        ? 'ring-2 ring-[#003366] ring-offset-2' 
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    data-testid={`thumbnail-${i}`}
                  >
                    <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover pointer-events-none select-none" draggable={false} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            {/* Collection & Category Tags */}
            <div className="flex items-center gap-2 mb-3">
              <Link to={`/shop?collection=${encodeURIComponent(product.collection_name)}`} className="text-xs uppercase tracking-wider text-gray-500 hover:text-[#003366] transition-colors">
                {product.collection_name}
              </Link>
              {product.category && (
                <>
                  <span className="text-gray-300">•</span>
                  <Link to={`/category/${product.category.toLowerCase().replace(' ', '-')}`} className="text-xs uppercase tracking-wider text-gray-500 hover:text-[#003366] transition-colors">
                    {product.category}
                  </Link>
                </>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-testid="product-name">{product.name}</h1>
            
            {/* SKU under title */}
            {product.sku && (
              <p className="text-sm text-gray-500 mb-4" data-testid="product-sku">SKU: {product.sku}</p>
            )}
            
            {/* Price - Was/Now Style */}
            <div className="mb-6">
              {product.rrp_price && product.rrp_price !== product.price ? (
                <div className="flex flex-col">
                  <span className="text-gray-500 line-through">Was {product.rrp_price}</span>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[#c41e3a] font-bold text-3xl" data-testid="product-price">{product.price}</span>
                    {discount > 0 && (
                      <span className="bg-[#c41e3a] text-white px-3 py-1 text-sm font-bold">
                        Save {discount}%
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <span className="text-gray-900 font-bold text-3xl" data-testid="product-price">{product.price}</span>
              )}
            </div>

            {/* Reviews Summary */}
            {product.review_count > 0 && (
              <div className="flex items-center gap-2 mb-6">
                <div className="flex">
                  {[1,2,3,4,5].map((star) => (
                    <Star 
                      key={star} 
                      className={`w-5 h-5 ${star <= product.avg_rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-500">({product.review_count} reviews)</span>
              </div>
            )}

            {/* Description - with HTML formatting */}
            {(product.description || product.body_html) && (
              <div className="mb-6">
                <div 
                  className="text-gray-600 leading-relaxed prose prose-sm max-w-none [&>p]:mb-3 [&>ul]:list-disc [&>ul]:ml-5 [&>ul]:mb-3 [&>ul>li]:mb-1 [&>ol]:list-decimal [&>ol]:ml-5 [&>ol]:mb-3 [&_strong]:font-semibold [&_strong]:text-gray-800 [&_br]:mb-2"
                  dangerouslySetInnerHTML={{ __html: product.body_html || product.description }}
                />
              </div>
            )}

            {/* AI-Generated Product Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {product.colour && (
                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1.5 text-sm font-medium rounded-full">
                  <span className="w-3 h-3 rounded-full bg-gray-400" style={{ backgroundColor: product.colour.toLowerCase().includes('white') ? '#f5f5f5' : product.colour.toLowerCase().includes('black') ? '#1a1a1a' : product.colour.toLowerCase().includes('grey') || product.colour.toLowerCase().includes('gray') ? '#808080' : product.colour.toLowerCase().includes('brown') ? '#8B4513' : product.colour.toLowerCase().includes('blue') ? '#4169E1' : product.colour.toLowerCase().includes('green') ? '#228B22' : product.colour.toLowerCase().includes('red') ? '#DC143C' : product.colour.toLowerCase().includes('oak') ? '#C19A6B' : product.colour.toLowerCase().includes('walnut') ? '#5D432C' : '#808080' }} />
                  {product.colour}
                </span>
              )}
              {product.material && (
                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1.5 text-sm font-medium rounded-full">
                  <Layers className="w-4 h-4" />
                  {product.material}
                </span>
              )}
              {product.product_type && (
                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1.5 text-sm font-medium rounded-full">
                  <Tag className="w-4 h-4" />
                  {product.product_type}
                </span>
              )}
              {product.category && !product.product_type && (
                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1.5 text-sm font-medium rounded-full">
                  <Tag className="w-4 h-4" />
                  {product.category}
                </span>
              )}
            </div>

            {/* Highlighted Delivery & Dimensions Box */}
            {(product.size_cm || product.assembled_weight_kg || product.number_of_boxes) && (
              <div className="mb-6 bg-[#f0f7ff] border-2 border-[#003366]/20 rounded-lg p-5" data-testid="product-info-box">
                <h3 className="font-bold text-[#003366] mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Product Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {product.size_cm && (
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                      <div className="text-[#003366] font-bold text-lg mb-1">Dimensions</div>
                      <div className="text-gray-700 text-sm">{product.size_cm}</div>
                      <div className="text-xs text-gray-500 mt-1">(H x W x D)</div>
                    </div>
                  )}
                  {product.assembled_weight_kg && (
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                      <div className="text-[#003366] font-bold text-lg mb-1">Weight</div>
                      <div className="text-gray-700 text-sm">{product.assembled_weight_kg} kg</div>
                      <div className="text-xs text-gray-500 mt-1">Assembled</div>
                    </div>
                  )}
                  {product.number_of_boxes && (
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                      <div className="text-[#003366] font-bold text-lg mb-1">Packaging</div>
                      <div className="text-gray-700 text-sm">{product.number_of_boxes} Box{product.number_of_boxes > 1 ? 'es' : ''}</div>
                      <div className="text-xs text-gray-500 mt-1">Self Assembly</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold text-gray-700">Quantity:</label>
                <div className="flex items-center border border-gray-200 rounded">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))} 
                    className="p-3 hover:bg-gray-100 transition-colors"
                    data-testid="qty-decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)} 
                    className="p-3 hover:bg-gray-100 transition-colors"
                    data-testid="qty-increase"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <Button 
                className="w-full bg-[#003366] hover:bg-[#004080] text-white font-bold py-4 text-lg"
                onClick={() => { for (let i = 0; i < quantity; i++) addToCart(product.id); }}
                data-testid="add-to-cart"
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                Add to Basket
              </Button>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1 border-gray-200"
                  onClick={addToWishlist}
                  data-testid="add-to-wishlist"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Wishlist
                </Button>
                <Button 
                  variant="outline" 
                  className={`flex-1 ${isInCompare ? 'bg-[#003366] text-white border-[#003366]' : 'border-gray-200'}`}
                  onClick={() => addToCompare(product)}
                  data-testid="compare-btn"
                >
                  <GitCompare className="w-5 h-5 mr-2" />
                  Compare
                </Button>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Truck className="w-5 h-5 text-[#003366]" />
                  <span>Free Delivery to UK Mainland</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <ShieldCheck className="w-5 h-5 text-[#003366]" />
                  <span>1 Year Guarantee</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Tag className="w-5 h-5 text-[#003366]" />
                  <span>Price Match Guaranteed</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <RotateCcw className="w-5 h-5 text-[#003366]" />
                  <span>Easy Returns</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recently Viewed Carousel on Product Page */}
      <RecentlyViewedCarousel />

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl bg-black/95 border-none">
          {uniqueImages[selectedImage] && (
            <img 
              src={uniqueImages[selectedImage]} 
              alt={product.name}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Collections Page - Furniture Village Style
const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    axios.get(`${API}/collections`).then(res => setCollections(res.data));
  }, []);

  return (
    <div className="bg-white min-h-screen" data-testid="collections-page">
      {/* Page Header */}
      <div className="bg-gray-50 py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#003366]">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">Collections</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Our Collections</h1>
          <p className="text-gray-600 mt-2">Explore our curated furniture ranges</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {collections.map((col) => (
            <Link 
              key={col.name}
              to={`/collection/${encodeURIComponent(col.name)}`}
              className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
            >
              <div className="aspect-square bg-gray-100 overflow-hidden">
                {col.image ? (
                  <img src={col.image} alt={col.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-12 h-12 text-gray-300" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 group-hover:text-[#003366] transition-colors">{col.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{col.count} products</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// Collection Detail Page - Shows all products in a specific collection
const CollectionDetailPage = () => {
  const { collectionName } = useParams();
  const [products, setProducts] = useState([]);
  const [collectionInfo, setCollectionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const sort = searchParams.get("sort") || "name";
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCollection = async () => {
      try {
        const decodedName = decodeURIComponent(collectionName);
        const [productsRes, collectionRes, descriptionRes] = await Promise.all([
          axios.get(`${API}/products?collection=${encodeURIComponent(decodedName)}&limit=100&sort=${sort}`),
          axios.get(`${API}/collections`),
          axios.get(`${API}/collection-descriptions/${encodeURIComponent(decodedName)}`)
        ]);

        const fallbackDescription = `Discover the ${decodedName} collection - beautifully crafted furniture designed for style and functionality.`;
        const selectedDescription = descriptionRes?.data?.description || fallbackDescription;

        setProducts(productsRes.data.products);
        
        // Get collection info
        const col = collectionRes.data.find(c => c.name === decodedName);
        setCollectionInfo({
          name: decodedName,
          image: col?.image || productsRes.data.products[0]?.images?.[0],
          description: selectedDescription,
          count: productsRes.data.total
        });
      } catch (e) {
        console.error('Failed to fetch collection');
      } finally {
        setLoading(false);
      }
    };
    fetchCollection();
  }, [collectionName, sort]);

  const updateSort = (newSort) => {
    navigate(`/collection/${collectionName}?sort=${newSort}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="collection-detail-page">
      {/* Hero Section */}
      <div className="relative h-64 md:h-80 bg-[#003366] overflow-hidden">
        {collectionInfo?.image && (
          <img 
            src={collectionInfo.image} 
            alt={collectionInfo?.name}
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#003366] to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center gap-2 text-sm text-white/70 mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/collections" className="hover:text-white">Collections</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{collectionInfo?.name}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-3">{collectionInfo?.name} Collection</h1>
          <p className="text-white/80 max-w-2xl">{collectionInfo?.description}</p>
          <p className="text-white/60 mt-2">{collectionInfo?.count} products</p>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Sort Bar */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-600">Showing {products.length} products</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Sort by:</span>
            <select 
              value={sort}
              onChange={(e) => updateSort(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="name">Name A-Z</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>

        {/* Products */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-16">
            <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No products found in this collection.</p>
          </div>
        )}
      </div>

      {/* Trust Badges */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-4">
              <Truck className="w-10 h-10 mx-auto text-[#003366] mb-3" />
              <h4 className="font-medium text-gray-900 mb-1">Free Delivery</h4>
              <p className="text-sm text-gray-500">Same-day dispatch</p>
            </div>
            <div className="text-center p-4">
              <ShieldCheck className="w-10 h-10 mx-auto text-[#003366] mb-3" />
              <h4 className="font-medium text-gray-900 mb-1">1 Year Guarantee</h4>
              <p className="text-sm text-gray-500">Quality assured</p>
            </div>
            <div className="text-center p-4">
              <RotateCcw className="w-10 h-10 mx-auto text-[#003366] mb-3" />
              <h4 className="font-medium text-gray-900 mb-1">Easy Returns</h4>
              <p className="text-sm text-gray-500">14 day returns</p>
            </div>
            <div className="text-center p-4">
              <Package className="w-10 h-10 mx-auto text-[#003366] mb-3" />
              <h4 className="font-medium text-gray-900 mb-1">Easy Assembly</h4>
              <p className="text-sm text-gray-500">Flatpack furniture</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Bedroom Page - Boca Living Style
const BedroomPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Bedroom categories with images
  const bedroomCategories = [
    { 
      name: "Bedside Cabinets", 
      image: "https://images.unsplash.com/photo-1757796859729-270d1e3ad4d5?auto=format&fit=crop&w=600&q=80",
      filter: "bedside"
    },
    { 
      name: "Chests of Drawers", 
      image: "https://images.unsplash.com/photo-1690310588492-fc8f92bff323?auto=format&fit=crop&w=600&q=80",
      filter: "chest"
    },
    { 
      name: "Wardrobes", 
      image: "https://images.unsplash.com/photo-1769690398694-9c5d5ca4b4ea?auto=format&fit=crop&w=600&q=80",
      filter: "wardrobe"
    },
    { 
      name: "Beds", 
      image: "https://images.unsplash.com/photo-1726108397211-6507220a6a21?auto=format&fit=crop&w=600&q=80",
      filter: "bed"
    },
    { 
      name: "Dressing Tables", 
      image: "https://images.unsplash.com/photo-1602164091410-255d1bf88959?auto=format&fit=crop&w=600&q=80",
      filter: "dressing"
    },
    { 
      name: "Storage", 
      image: "https://images.unsplash.com/photo-1646061142491-fc141798ba14?auto=format&fit=crop&w=600&q=80",
      filter: "storage"
    }
  ];

  useEffect(() => {
    // Fetch collections that have bedroom products
    axios.get(`${API}/products?category=Bedroom&limit=100`).then(res => {
      const uniqueCollections = [...new Set(res.data.products.map(p => p.collection_name).filter(Boolean))];
      
      // Get collection details with images
      const collectionDetails = uniqueCollections.map(name => {
        const collectionProducts = res.data.products.filter(p => p.collection_name === name);
        const featuredProduct = collectionProducts[0];
        return {
          name,
          description: getCollectionDescription(name),
          image: featuredProduct?.images?.[0] || "https://images.unsplash.com/photo-1540518614846-7eded433c457?auto=format&fit=crop&w=800&q=80",
          productCount: collectionProducts.length
        };
      });
      
      setCollections(collectionDetails);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const getCollectionDescription = (name) => {
    const descriptions = {
      "Angel": "The Angel collection is perfect for all age groups, finished in a modern hard wearing white crafted oak melamine.",
      "Barcelona": "This exquisite collection highlights a distinctive traditional shaker style with meticulous attention to detail, adding a timeless charm to your bedroom.",
      "Chelsea": "With crisp lines and a stunning white high gloss finish, it's easy to see why Chelsea is one of our bestsellers for bedrooms.",
      "Oslo": "Alluding a retro modern charm, the Oslo collection has an authentic Scandinavian appeal with real style.",
      "Paris": "Romantically styled, sophisticated bedroom furniture with a beautiful classic feel. A collection with true Parisian qualities.",
      "Madrid": "Elegant shaker style! This Madrid collection is complete with simple round metal handle that adds class to this modern collection.",
      "Naia": "Modern storage solutions with carefully designed soft edges for a clean, linear design. Four colours to choose from.",
      "Nova": "All cabinets and chests in the Nova feature a modern handleless drawer solution, available in light oak, truffle oak, and white finishes.",
      "Pepe": "A practical and affordable collection of storage solutions designed for the bedroom. Pepe's range is comfortable in any room of the house.",
      "Sofia": "Spacious storage solutions that will have you covered. Organisation is easy with the deep drawers that this beautiful collection has to offer."
    };
    return descriptions[name] || "Discover our beautiful collection of bedroom furniture, designed to transform your space into a haven of comfort and style.";
  };

  return (
    <div className="min-h-screen bg-white" data-testid="bedroom-page">
      {/* Hero Section */}
      <div className="bg-[#003366] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">Bedroom</h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg">
            Transform your bedroom into a haven of comfort and peace. From cosy beds to spacious wardrobes, 
            our range has everything you need.
          </p>
        </div>
      </div>

      {/* Category Tiles */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="bedroom-categories">
          {bedroomCategories.map((cat) => (
            <Link 
              key={cat.name}
              to={`/category/bedroom?type=${cat.filter}`}
              className="group relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all"
              data-testid={`bedroom-cat-${cat.filter}`}
            >
              <img 
                src={cat.image} 
                alt={cat.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white text-sm md:text-base font-semibold text-center">{cat.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Collections Showcase */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-serif text-3xl text-center text-[#003366] mb-4">Our Bedroom Collections</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Explore our curated bedroom collections - each designed with style and functionality in mind.
          </p>

          {loading ? (
            <div className="space-y-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/2 aspect-[16/9] bg-gray-200 rounded-lg" />
                  <div className="md:w-1/2 flex flex-col justify-center">
                    <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-16" data-testid="bedroom-collections">
              {collections.map((collection, index) => (
                <div 
                  key={collection.name}
                  className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}
                  data-testid={`collection-${collection.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {/* Image */}
                  <div className="md:w-1/2">
                    <Link 
                      to={`/collection/${encodeURIComponent(collection.name)}`}
                      className="block group overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img 
                          src={collection.image} 
                          alt={collection.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                      </div>
                    </Link>
                  </div>

                  {/* Content */}
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pl-8' : 'md:pr-8'}`}>
                    <h3 className="font-serif text-2xl md:text-3xl text-[#003366] mb-4">{collection.name}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{collection.description}</p>
                    <div className="flex items-center gap-4">
                      <Link 
                        to={`/collection/${encodeURIComponent(collection.name)}`}
                        className="inline-flex items-center gap-2 bg-[#003366] text-white px-6 py-3 rounded-md hover:bg-[#002244] transition-colors font-medium"
                      >
                        SHOP NOW
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                      <span className="text-sm text-gray-500">{collection.productCount} products</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Banner */}
      <div className="bg-[#003366] py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-white mb-4">
            Free Delivery to UK Mainland
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Order before 3pm for same-day dispatch! Transform your bedroom with our quality furniture.
          </p>
          <Link 
            to="/category/bedroom"
            className="inline-block bg-white text-[#003366] px-8 py-3 rounded-md hover:bg-gray-100 transition-colors font-medium"
          >
            Browse All Bedroom Furniture
          </Link>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-4">
            <Truck className="w-10 h-10 mx-auto text-[#003366] mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">Free Delivery</h4>
            <p className="text-sm text-gray-500">Same-day dispatch</p>
          </div>
          <div className="text-center p-4">
            <ShieldCheck className="w-10 h-10 mx-auto text-[#003366] mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">1 Year Guarantee</h4>
            <p className="text-sm text-gray-500">Quality assured</p>
          </div>
          <div className="text-center p-4">
            <RotateCcw className="w-10 h-10 mx-auto text-[#003366] mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">Easy Returns</h4>
            <p className="text-sm text-gray-500">14 day returns</p>
          </div>
          <div className="text-center p-4">
            <Package className="w-10 h-10 mx-auto text-[#003366] mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">Easy Assembly</h4>
            <p className="text-sm text-gray-500">Flatpack furniture</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Living Room Page - Boca Living Style
const LivingRoomPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { name: "TV Units", image: "https://images.unsplash.com/photo-1762280237536-90b449615e8e?auto=format&fit=crop&w=600&q=80", filter: "tv" },
    { name: "Coffee Tables", image: "https://images.unsplash.com/photo-1658893702377-ee7b3eafd6d1?auto=format&fit=crop&w=600&q=80", filter: "coffee" },
    { name: "Bookcases", image: "https://images.unsplash.com/photo-1620388640952-35a1d22d158d?auto=format&fit=crop&w=600&q=80", filter: "bookcase" },
    { name: "Sideboards", image: "https://images.unsplash.com/photo-1762280237536-90b449615e8e?auto=format&fit=crop&w=600&q=80", filter: "sideboard" },
    { name: "Display Cabinets", image: "https://images.unsplash.com/photo-1764337522032-7bb182843f7c?auto=format&fit=crop&w=600&q=80", filter: "display" },
    { name: "Storage Units", image: "https://images.unsplash.com/photo-1620388640952-35a1d22d158d?auto=format&fit=crop&w=600&q=80", filter: "storage" }
  ];

  useEffect(() => {
    axios.get(`${API}/products?category=Living&limit=100`).then(res => {
      const uniqueCollections = [...new Set(res.data.products.map(p => p.collection_name).filter(Boolean))];
      const collectionDetails = uniqueCollections.map(name => {
        const collectionProducts = res.data.products.filter(p => p.collection_name === name);
        const featuredProduct = collectionProducts[0];
        return {
          name,
          description: getLivingCollectionDescription(name),
          image: featuredProduct?.images?.[0] || "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=800&q=80",
          productCount: collectionProducts.length
        };
      });
      setCollections(collectionDetails);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const getLivingCollectionDescription = (name) => {
    const descriptions = {
      "Angel": "Create a bright and welcoming living space with the Angel collection's modern white crafted oak finish.",
      "Barcelona": "The Barcelona collection brings distinctive shaker style elegance to your living room with meticulous attention to detail.",
      "Chelsea": "With crisp lines and stunning white high gloss finish, Chelsea transforms your living space into a contemporary haven.",
      "Oslo": "The Oslo collection brings authentic Scandinavian charm to your living room with its retro modern appeal.",
      "Paris": "Add romantic sophistication to your living space with the Paris collection's beautiful classic feel.",
      "Naia": "Modern storage solutions with carefully designed soft edges for a clean, linear living room design."
    };
    return descriptions[name] || "Transform your living space with our beautifully crafted furniture collection, designed for style and functionality.";
  };

  return (
    <RoomPageTemplate
      roomName="Living Room"
      roomDescription="Create the perfect living space for relaxation and entertainment. From stylish TV units to elegant sideboards, our range has everything you need."
      categories={categories}
      collections={collections}
      loading={loading}
      categoryPath="living"
    />
  );
};

// Dining Room Page - Boca Living Style
const DiningRoomPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { name: "Dining Tables", image: "https://images.unsplash.com/photo-1758977404683-d04c315a005b?auto=format&fit=crop&w=600&q=80", filter: "table" },
    { name: "Dining Chairs", image: "https://images.unsplash.com/photo-1758977405163-f2595de08dfe?auto=format&fit=crop&w=600&q=80", filter: "chair" },
    { name: "Bar Stools", image: "https://images.unsplash.com/photo-1767773796788-eec9640ccfc0?auto=format&fit=crop&w=600&q=80", filter: "stool" },
    { name: "Sideboards", image: "https://images.unsplash.com/photo-1762280237536-90b449615e8e?auto=format&fit=crop&w=600&q=80", filter: "sideboard" },
    { name: "Display Cabinets", image: "https://images.unsplash.com/photo-1764337522032-7bb182843f7c?auto=format&fit=crop&w=600&q=80", filter: "display" },
    { name: "Corner Units", image: "https://images.unsplash.com/photo-1620388640952-35a1d22d158d?auto=format&fit=crop&w=600&q=80", filter: "corner" }
  ];

  useEffect(() => {
    axios.get(`${API}/products?category=Dining&limit=100`).then(res => {
      const uniqueCollections = [...new Set(res.data.products.map(p => p.collection_name).filter(Boolean))];
      const collectionDetails = uniqueCollections.map(name => {
        const collectionProducts = res.data.products.filter(p => p.collection_name === name);
        const featuredProduct = collectionProducts[0];
        return {
          name,
          description: getDiningCollectionDescription(name),
          image: featuredProduct?.images?.[0] || "https://images.unsplash.com/photo-1617806118233-18e1de247200?auto=format&fit=crop&w=800&q=80",
          productCount: collectionProducts.length
        };
      });
      setCollections(collectionDetails);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const getDiningCollectionDescription = (name) => {
    const descriptions = {
      "Angel": "The Angel dining collection offers a perfect setting for family meals with its modern white crafted oak finish.",
      "Barcelona": "Create memorable dining experiences with Barcelona's distinctive shaker style and elegant craftsmanship.",
      "Chelsea": "Elevate your dining room with Chelsea's crisp lines and stunning white high gloss finish.",
      "Oslo": "Bring Scandinavian charm to your dining space with the Oslo collection's retro modern design.",
      "Paris": "Set the scene for romantic dinners with the Paris collection's sophisticated French-inspired design."
    };
    return descriptions[name] || "Create the perfect dining experience with our beautifully crafted furniture collection.";
  };

  return (
    <RoomPageTemplate
      roomName="Dining Room"
      roomDescription="Create the perfect setting for memorable meals and gatherings. From elegant dining tables to stylish sideboards, find everything for your dining space."
      categories={categories}
      collections={collections}
      loading={loading}
      categoryPath="dining"
    />
  );
};

// Kids Room Page - Boca Living Style
const KidsRoomPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { name: "Kids Beds", image: "https://images.unsplash.com/photo-1771838230544-aac51a5fcd24?auto=format&fit=crop&w=600&q=80", filter: "bed" },
    { name: "Bunk Beds", image: "https://images.unsplash.com/photo-1771838230544-aac51a5fcd24?auto=format&fit=crop&w=600&q=80", filter: "bunk" },
    { name: "Wardrobes", image: "https://images.unsplash.com/photo-1769690398694-9c5d5ca4b4ea?auto=format&fit=crop&w=600&q=80", filter: "wardrobe" },
    { name: "Desks", image: "https://images.unsplash.com/photo-1724402022764-b63d2382834f?auto=format&fit=crop&w=600&q=80", filter: "desk" },
    { name: "Storage", image: "https://images.unsplash.com/photo-1620388640952-35a1d22d158d?auto=format&fit=crop&w=600&q=80", filter: "storage" },
    { name: "Bookcases", image: "https://images.unsplash.com/photo-1620388640952-35a1d22d158d?auto=format&fit=crop&w=600&q=80", filter: "bookcase" }
  ];

  useEffect(() => {
    axios.get(`${API}/products?category=Kids&limit=100`).then(res => {
      const uniqueCollections = [...new Set(res.data.products.map(p => p.collection_name).filter(Boolean))];
      const collectionDetails = uniqueCollections.map(name => {
        const collectionProducts = res.data.products.filter(p => p.collection_name === name);
        const featuredProduct = collectionProducts[0];
        return {
          name,
          description: getKidsCollectionDescription(name),
          image: featuredProduct?.images?.[0] || "https://images.unsplash.com/photo-1540518614846-7eded433c457?auto=format&fit=crop&w=800&q=80",
          productCount: collectionProducts.length
        };
      });
      setCollections(collectionDetails);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const getKidsCollectionDescription = (name) => {
    const descriptions = {
      "4YOU": "The 4YOU collection offers modern, versatile furniture perfect for growing children and teens.",
      "Angel": "Create a bright and cheerful kids room with the Angel collection's playful yet practical design."
    };
    return descriptions[name] || "Create a fun and functional space for your little ones with our kids furniture collection.";
  };

  return (
    <RoomPageTemplate
      roomName="Kids"
      roomDescription="Create magical spaces for your little ones. From fun bunk beds to practical storage solutions, find furniture that grows with your children."
      categories={categories}
      collections={collections}
      loading={loading}
      categoryPath="kids"
    />
  );
};

// Home Office Page - Boca Living Style
const HomeOfficePage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { name: "Desks", image: "https://images.unsplash.com/photo-1724402022764-b63d2382834f?auto=format&fit=crop&w=600&q=80", filter: "desk" },
    { name: "Office Chairs", image: "https://images.unsplash.com/photo-1688578735122-f37256f1b8b0?auto=format&fit=crop&w=600&q=80", filter: "chair" },
    { name: "Bookcases", image: "https://images.unsplash.com/photo-1620388640952-35a1d22d158d?auto=format&fit=crop&w=600&q=80", filter: "bookcase" },
    { name: "Filing Cabinets", image: "https://images.unsplash.com/photo-1762280237536-90b449615e8e?auto=format&fit=crop&w=600&q=80", filter: "filing" },
    { name: "Shelving", image: "https://images.unsplash.com/photo-1620388640952-35a1d22d158d?auto=format&fit=crop&w=600&q=80", filter: "shelving" },
    { name: "Storage", image: "https://images.unsplash.com/photo-1620388640952-35a1d22d158d?auto=format&fit=crop&w=600&q=80", filter: "storage" }
  ];

  useEffect(() => {
    axios.get(`${API}/products?category=Home Office&limit=100`).then(res => {
      const uniqueCollections = [...new Set(res.data.products.map(p => p.collection_name).filter(Boolean))];
      const collectionDetails = uniqueCollections.map(name => {
        const collectionProducts = res.data.products.filter(p => p.collection_name === name);
        const featuredProduct = collectionProducts[0];
        return {
          name,
          description: getOfficeCollectionDescription(name),
          image: featuredProduct?.images?.[0] || "https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd?auto=format&fit=crop&w=800&q=80",
          productCount: collectionProducts.length
        };
      });
      setCollections(collectionDetails);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const getOfficeCollectionDescription = (name) => {
    const descriptions = {
      "Function": "The Function collection provides practical and stylish solutions for your home office needs.",
      "Prima": "Prima offers sleek, contemporary office furniture designed for the modern professional."
    };
    return descriptions[name] || "Create a productive workspace with our professional home office furniture collection.";
  };

  return (
    <RoomPageTemplate
      roomName="Home Office"
      roomDescription="Create your perfect workspace at home. From ergonomic desks to smart storage solutions, find everything you need for a productive home office."
      categories={categories}
      collections={collections}
      loading={loading}
      categoryPath="home-office"
    />
  );
};

// Packages Page - Boca Living Style
const PackagesPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { name: "Bedroom Packages", image: "https://images.unsplash.com/photo-1726108397211-6507220a6a21?auto=format&fit=crop&w=600&q=80", filter: "bedroom" },
    { name: "Living Room Sets", image: "https://images.unsplash.com/photo-1763565909003-46e9dfb68a00?auto=format&fit=crop&w=600&q=80", filter: "living" },
    { name: "Dining Sets", image: "https://images.unsplash.com/photo-1758977404683-d04c315a005b?auto=format&fit=crop&w=600&q=80", filter: "dining" },
    { name: "Office Bundles", image: "https://images.unsplash.com/photo-1724402022764-b63d2382834f?auto=format&fit=crop&w=600&q=80", filter: "office" },
    { name: "Kids Room Sets", image: "https://images.unsplash.com/photo-1771838230544-aac51a5fcd24?auto=format&fit=crop&w=600&q=80", filter: "kids" },
    { name: "Starter Packs", image: "https://images.unsplash.com/photo-1620388640952-35a1d22d158d?auto=format&fit=crop&w=600&q=80", filter: "starter" }
  ];

  useEffect(() => {
    axios.get(`${API}/products?category=Packages&limit=100`).then(res => {
      const uniqueCollections = [...new Set(res.data.products.map(p => p.collection_name).filter(Boolean))];
      const collectionDetails = uniqueCollections.map(name => {
        const collectionProducts = res.data.products.filter(p => p.collection_name === name);
        const featuredProduct = collectionProducts[0];
        return {
          name,
          description: "Complete furniture packages at great value prices. Everything you need in one convenient bundle.",
          image: featuredProduct?.images?.[0] || "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=800&q=80",
          productCount: collectionProducts.length
        };
      });
      setCollections(collectionDetails);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <RoomPageTemplate
      roomName="Packages"
      roomDescription="Save time and money with our curated furniture packages. Complete room solutions designed to work together perfectly."
      categories={categories}
      collections={collections}
      loading={loading}
      categoryPath="packages"
    />
  );
};

// Reusable Room Page Template Component
const RoomPageTemplate = ({ roomName, roomDescription, categories, collections, loading, categoryPath }) => {
  return (
    <div className="min-h-screen bg-white" data-testid={`${categoryPath}-page`}>
      {/* Hero Section */}
      <div className="bg-[#003366] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">{roomName}</h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg">{roomDescription}</p>
        </div>
      </div>

      {/* Category Tiles */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid={`${categoryPath}-categories`}>
          {categories.map((cat) => (
            <Link 
              key={cat.name}
              to={`/category/${categoryPath}?type=${cat.filter}`}
              className="group relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all"
            >
              <img 
                src={cat.image} 
                alt={cat.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white text-sm md:text-base font-semibold text-center">{cat.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Collections Showcase */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-serif text-3xl text-center text-[#003366] mb-4">Our {roomName} Collections</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Explore our curated {roomName.toLowerCase()} collections - each designed with style and functionality in mind.
          </p>

          {loading ? (
            <div className="space-y-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/2 aspect-[16/9] bg-gray-200 rounded-lg" />
                  <div className="md:w-1/2 flex flex-col justify-center">
                    <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : collections.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No collections available yet. Browse all products instead.</p>
              <Link 
                to={`/category/${categoryPath}`}
                className="inline-block mt-4 bg-[#003366] text-white px-6 py-3 rounded-md hover:bg-[#002244] transition-colors"
              >
                Browse All {roomName} Furniture
              </Link>
            </div>
          ) : (
            <div className="space-y-16" data-testid={`${categoryPath}-collections`}>
              {collections.map((collection, index) => (
                <div 
                  key={collection.name}
                  className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}
                >
                  {/* Image */}
                  <div className="md:w-1/2">
                    <Link 
                      to={`/collection/${encodeURIComponent(collection.name)}`}
                      className="block group overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img 
                          src={collection.image} 
                          alt={collection.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                      </div>
                    </Link>
                  </div>

                  {/* Content */}
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pl-8' : 'md:pr-8'}`}>
                    <h3 className="font-serif text-2xl md:text-3xl text-[#003366] mb-4">{collection.name}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{collection.description}</p>
                    <div className="flex items-center gap-4">
                      <Link 
                        to={`/collection/${encodeURIComponent(collection.name)}`}
                        className="inline-flex items-center gap-2 bg-[#003366] text-white px-6 py-3 rounded-md hover:bg-[#002244] transition-colors font-medium"
                      >
                        SHOP NOW
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                      <span className="text-sm text-gray-500">{collection.productCount} products</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Banner */}
      <div className="bg-[#003366] py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-white mb-4">Free Delivery to UK Mainland</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Order before 3pm for same-day dispatch! Transform your {roomName.toLowerCase()} with our quality furniture.
          </p>
          <Link 
            to={`/category/${categoryPath}`}
            className="inline-block bg-white text-[#003366] px-8 py-3 rounded-md hover:bg-gray-100 transition-colors font-medium"
          >
            Browse All {roomName} Furniture
          </Link>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-4">
            <Truck className="w-10 h-10 mx-auto text-[#003366] mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">Free Delivery</h4>
            <p className="text-sm text-gray-500">Same-day dispatch</p>
          </div>
          <div className="text-center p-4">
            <ShieldCheck className="w-10 h-10 mx-auto text-[#003366] mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">1 Year Guarantee</h4>
            <p className="text-sm text-gray-500">Quality assured</p>
          </div>
          <div className="text-center p-4">
            <RotateCcw className="w-10 h-10 mx-auto text-[#003366] mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">Easy Returns</h4>
            <p className="text-sm text-gray-500">14 day returns</p>
          </div>
          <div className="text-center p-4">
            <Package className="w-10 h-10 mx-auto text-[#003366] mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">Easy Assembly</h4>
            <p className="text-sm text-gray-500">Flatpack furniture</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Category Page
const CategoryPage = () => {
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [collections, setCollections] = useState([]);
  const [productTypes, setProductTypes] = useState([]);

  const categoryMap = {
    'bedroom': 'Bedroom',
    'dining': 'Dining',
    'living': 'Living',
    'kids': 'Kids',
    'home-office': 'Home Office',
    'packages': 'Packages'
  };

  const categoryName = categoryMap[categoryId] || categoryId;
  const sort = searchParams.get("sort") || "name";
  const page = parseInt(searchParams.get("page") || "1");
  const collection = searchParams.get("collection") || "";
  const typeFilter = searchParams.get("type") || "";

  // Map type filters to search terms for product names
  const typeFilterMap = {
    // Bedroom
    'bedside': 'bedside',
    'chest': 'chest',
    'wardrobe': 'wardrobe',
    'bed': 'bed',
    'dressing': 'dressing',
    'storage': 'storage',
    // Living
    'tv': 'tv',
    'coffee': 'coffee',
    'bookcase': 'bookcase',
    'sideboard': 'sideboard',
    'display': 'display',
    // Dining
    'table': 'table',
    'chair': 'chair',
    'stool': 'stool',
    'corner': 'corner',
    // Kids
    'bunk': 'bunk',
    'desk': 'desk',
    // Office
    'filing': 'filing',
    'shelving': 'shelf',
    // Packages
    'bedroom': 'bedroom',
    'living': 'living',
    'dining': 'dining',
    'office': 'office',
    'kids': 'kids',
    'starter': 'starter'
  };

  const searchTerm = typeFilterMap[typeFilter] || typeFilter;

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("category", categoryName);
    if (collection) params.append("collection", collection);
    if (searchTerm) params.append("search", searchTerm);
    params.append("sort", sort);
    params.append("page", page.toString());
    params.append("limit", "12");

    axios.get(`${API}/products?${params}`).then(res => {
      setProducts(res.data.products);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setCollections(res.data.collections || []);
      setProductTypes(res.data.product_types || []);
      setLoading(false);
    });
  }, [categoryName, sort, page, collection, searchTerm]);

  const updateParams = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    if (key !== "page") newParams.set("page", "1");
    setSearchParams(newParams);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="category-page">
      {/* Hero Banner */}
      <div className="relative h-48 md:h-64 rounded-xl overflow-hidden mb-12 bg-gradient-to-r from-[#003366] to-[#003366]/80">
        <div className="absolute inset-0 flex items-center justify-center text-center">
          <div>
            <h1 className="font-serif text-4xl md:text-5xl text-white mb-2">
              {typeFilter ? `${typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1)} - ${categoryName}` : categoryName}
            </h1>
            <p className="text-white/70">{total} products</p>
          </div>
        </div>
      </div>

      {/* Active Filter Badge */}
      {typeFilter && (
        <div className="mb-6 flex items-center gap-2">
          <span className="text-sm text-gray-600">Filtered by:</span>
          <span className="inline-flex items-center gap-1 bg-[#003366] text-white px-3 py-1 rounded-full text-sm">
            {typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1)}
            <button 
              onClick={() => updateParams("type", "")}
              className="ml-1 hover:bg-white/20 rounded-full p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
          <Link 
            to={`/${categoryId}`}
            className="text-sm text-[#003366] hover:underline"
          >
            View all {categoryName}
          </Link>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Filters */}
        <div className="md:w-64 flex-shrink-0">
          <h3 className="font-medium text-warm-dark mb-4">Filters</h3>
          
          <div className="space-y-4">
            {collections.length > 0 && (
              <div>
                <label className="text-sm font-medium text-warm-muted block mb-2">Collection</label>
                <Select value={collection || "all"} onValueChange={(v) => updateParams("collection", v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Collections" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Collections</SelectItem>
                    {collections.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">Sort By</label>
              <Select value={sort} onValueChange={(v) => updateParams("sort", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name A-Z</SelectItem>
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-warm-subtle mb-4 rounded-lg" />
                  <div className="h-4 bg-warm-subtle mb-2 w-1/3" />
                  <div className="h-5 bg-warm-subtle mb-2" />
                  <div className="h-4 bg-warm-subtle w-1/4" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-12 h-12 mx-auto text-warm-subtle mb-4" />
              <p className="text-warm-muted">No products found in this category</p>
              <Link to="/shop" className="text-accent-terracotta mt-2 inline-block">Browse all products</Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="category-products-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={page === 1}
                    onClick={() => updateParams("page", (page - 1).toString())}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-4 text-sm">Page {page} of {totalPages}</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => updateParams("page", (page + 1).toString())}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Customer Service Pages
const CustomerServicePage = () => {
  const { pageId } = useParams();
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/customer-service/pages`).then(res => setPages(res.data));
  }, []);

  useEffect(() => {
    if (pageId) {
      setLoading(true);
      axios.get(`${API}/customer-service/page/${pageId}`).then(res => {
        setCurrentPage(res.data);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [pageId]);

  // Index page (no pageId)
  if (!pageId) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="customer-service-page">
        <h1 className="font-serif text-4xl text-center mb-4">Customer Service</h1>
        <p className="text-center text-warm-muted mb-12 max-w-xl mx-auto">
          We're here to help! Find answers to common questions or get in touch with our friendly support team.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pages.map((page) => (
            <Link 
              key={page.id}
              to={`/customer-service/${page.id}`}
              className="p-6 bg-white border border-warm rounded-lg hover:shadow-warm transition-shadow group"
              data-testid={`cs-link-${page.id}`}
            >
              <h3 className="font-serif text-lg text-warm-dark mb-2 group-hover:text-accent-terracotta transition-colors">{page.title}</h3>
              <ChevronRight className="w-5 h-5 text-warm-muted group-hover:text-accent-terracotta transition-colors" />
            </Link>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-16 bg-warm-paper rounded-xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-serif text-2xl text-warm-dark mb-4">Get in Touch</h2>
              <p className="text-warm-muted mb-6">
                Can't find what you're looking for? Our customer service team is ready to assist you.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-terracotta/10 rounded-full flex items-center justify-center">
                    <span className="text-accent-terracotta">✉</span>
                  </div>
                  <div>
                    <p className="text-sm text-warm-muted">Email</p>
                    <p className="font-medium text-warm-dark">support@vocoliving.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-terracotta/10 rounded-full flex items-center justify-center">
                    <span className="text-accent-terracotta">📞</span>
                  </div>
                  <div>
                    <p className="text-sm text-warm-muted">Phone</p>
                    <p className="font-medium text-warm-dark">0800 123 4567</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-terracotta/10 rounded-full flex items-center justify-center">
                    <span className="text-accent-terracotta">🕐</span>
                  </div>
                  <div>
                    <p className="text-sm text-warm-muted">Hours</p>
                    <p className="font-medium text-warm-dark">Mon-Fri, 9am-5pm</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-6">
              <h3 className="font-serif text-xl text-warm-dark mb-4">Quick Links</h3>
              <div className="space-y-3">
                <Link to="/customer-service/delivery" className="block text-warm-muted hover:text-accent-terracotta transition-colors">
                  → Delivery Information
                </Link>
                <Link to="/customer-service/returns" className="block text-warm-muted hover:text-accent-terracotta transition-colors">
                  → Returns & Refunds
                </Link>
                <Link to="/customer-service/faq" className="block text-warm-muted hover:text-accent-terracotta transition-colors">
                  → FAQ
                </Link>
                <Link to="/customer-service/warranty" className="block text-warm-muted hover:text-accent-terracotta transition-colors">
                  → Product Warranty
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Individual page
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="h-8 bg-warm-subtle w-1/3 mb-4 animate-pulse" />
        <div className="h-4 bg-warm-subtle w-full mb-2 animate-pulse" />
        <div className="h-4 bg-warm-subtle w-2/3 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid={`customer-service-${pageId}`}>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-warm-muted mb-8">
        <Link to="/" className="hover:text-warm-dark">Home</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/customer-service" className="hover:text-warm-dark">Customer Service</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-warm-dark">{currentPage?.title}</span>
      </nav>

      <h1 className="font-serif text-4xl text-warm-dark mb-8">{currentPage?.title}</h1>
      
      <div className="prose prose-warm max-w-none">
        {currentPage?.content?.split('\n').map((paragraph, i) => (
          <p key={i} className="text-warm-muted mb-4 whitespace-pre-wrap">{paragraph}</p>
        ))}
      </div>

      {/* Back Link */}
      <div className="mt-12 pt-8 border-t border-warm">
        <Link to="/customer-service" className="text-accent-terracotta hover:underline flex items-center gap-2">
          <ChevronLeft className="w-4 h-4" />
          Back to Customer Service
        </Link>
      </div>
    </div>
  );
};

// Auth Pages
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(redirect);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <h1 className="font-serif text-3xl text-center mb-8">Sign In</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="text-sm font-medium text-warm-muted block mb-2">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-warm-muted block mb-2">Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
          </div>
          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-accent-terracotta hover:underline">
              Forgot password?
            </Link>
          </div>
          <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="login-submit">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
        <p className="text-center mt-6 text-warm-muted">
          Don't have an account? <Link to="/register" className="text-accent-terracotta">Create one</Link>
        </p>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({ email: "", password: "", first_name: "", last_name: "" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      toast.success("Account created successfully!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <h1 className="font-serif text-3xl text-center mb-8">Create Account</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">First Name</label>
              <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required data-testid="register-firstname" />
            </div>
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">Last Name</label>
              <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required data-testid="register-lastname" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-warm-muted block mb-2">Email</label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="register-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-warm-muted block mb-2">Password</label>
            <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required data-testid="register-password" />
          </div>
          <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="register-submit">
            {loading ? "Creating account..." : "Create Account"}
          </Button>
        </form>
        <p className="text-center mt-6 text-warm-muted">
          Already have an account? <Link to="/login" className="text-accent-terracotta">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

// Account Page
const AccountPage = () => {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    axios.get(`${API}/orders`).then(res => setOrders(res.data));
  }, [user, navigate]);

  if (!user) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      out_for_delivery: "bg-indigo-100 text-indigo-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="account-page">
      <h1 className="font-serif text-3xl mb-8">My Account</h1>
      
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-1">
          <div className="bg-warm-paper p-6 rounded-lg">
            <h2 className="font-serif text-xl mb-4">Profile</h2>
            <p className="font-medium">{user.first_name} {user.last_name}</p>
            <p className="text-warm-muted">{user.email}</p>
            <button onClick={logout} className="mt-4 text-red-600 text-sm font-medium flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>

        <div className="md:col-span-2">
          <h2 className="font-serif text-xl mb-4">Order History</h2>
          {orders.length === 0 ? (
            <div className="text-center py-12 bg-warm-paper rounded-lg">
              <Package className="w-12 h-12 mx-auto text-warm-subtle mb-4" />
              <p className="text-warm-muted">No orders yet</p>
              <Link to="/shop" className="text-accent-terracotta mt-2 inline-block">Start Shopping</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div key={order.id} className="border border-warm rounded-lg p-4 hover:shadow-warm transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm">Order #{order.id.slice(0, 8).toUpperCase()}</span>
                    <Badge className={getStatusColor(order.status)}>{order.status?.replace("_", " ").toUpperCase()}</Badge>
                  </div>
                  <p className="text-warm-muted text-sm">{new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  <p className="font-medium mt-2">£{order.total?.toFixed(2)}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <div className="flex gap-2 flex-1">
                      {order.items?.slice(0, 3).map((item, i) => (
                        item.image && <img key={i} src={item.image} alt="" className="w-12 h-12 object-cover rounded" />
                      ))}
                      {order.items?.length > 3 && <span className="text-sm text-warm-muted self-center">+{order.items.length - 3} more</span>}
                    </div>
                    <Link 
                      to={`/order/${order.id}/track`}
                      className="text-accent-terracotta text-sm font-medium hover:underline flex items-center gap-1"
                    >
                      <Truck className="w-4 h-4" /> Track Order
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Order Tracking Page
const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login?redirect=/order/" + orderId + "/track");
      return;
    }
    
    setLoading(true);
    axios.get(`${API}/orders/${orderId}/track`)
      .then(res => {
        setTracking(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || "Order not found");
        setLoading(false);
      });
  }, [orderId, user, navigate]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-warm-subtle w-1/3 mb-8" />
          <div className="h-4 bg-warm-subtle w-full mb-4" />
          <div className="h-20 bg-warm-subtle w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Package className="w-16 h-16 text-warm-subtle mx-auto mb-6" />
        <h1 className="font-serif text-2xl mb-4">Order Not Found</h1>
        <p className="text-warm-muted mb-6">{error}</p>
        <Link to="/account">
          <Button variant="outline">Back to My Orders</Button>
        </Link>
      </div>
    );
  }

  const getStepIcon = (step, completed, current) => {
    if (completed && !current) return <CheckCircle className="w-6 h-6 text-green-500" />;
    if (current) return <div className="w-6 h-6 rounded-full bg-accent-terracotta animate-pulse" />;
    return <div className="w-6 h-6 rounded-full border-2 border-warm-subtle" />;
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="order-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link to="/account" className="text-sm text-warm-muted hover:text-warm-dark flex items-center gap-1 mb-2">
            <ChevronLeft className="w-4 h-4" /> Back to Orders
          </Link>
          <h1 className="font-serif text-3xl">Order #{tracking.order_number}</h1>
        </div>
        <Badge className={`text-sm px-3 py-1 ${
          tracking.status === 'delivered' ? 'bg-green-100 text-green-800' :
          tracking.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
          tracking.status === 'processing' ? 'bg-blue-100 text-blue-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {tracking.status_label}
        </Badge>
      </div>

      {/* Progress Tracker */}
      <div className="bg-white border border-warm rounded-xl p-6 mb-8" data-testid="progress-tracker">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium text-warm-dark">Order Progress</h2>
          <span className="text-sm text-warm-muted">{tracking.progress.percentage}% Complete</span>
        </div>
        
        {/* Progress Bar */}
        <div className="relative h-2 bg-warm-subtle rounded-full mb-8 overflow-hidden">
          <div 
            className="absolute h-full bg-gradient-to-r from-accent-terracotta to-green-500 rounded-full transition-all duration-500"
            style={{ width: `${tracking.progress.percentage}%` }}
          />
        </div>

        {/* Steps */}
        <div className="flex justify-between relative">
          {tracking.progress.steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center relative z-10" style={{ width: `${100 / tracking.progress.steps.length}%` }}>
              <div className={`mb-3 ${step.completed ? 'text-green-500' : step.current ? 'text-accent-terracotta' : 'text-warm-subtle'}`}>
                {getStepIcon(step, step.completed, step.current)}
              </div>
              <span className={`text-xs text-center ${step.completed || step.current ? 'text-warm-dark font-medium' : 'text-warm-muted'}`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Tracking Info */}
      {(tracking.tracking_number || tracking.carrier || tracking.estimated_delivery) && (
        <div className="bg-warm-paper rounded-xl p-6 mb-8">
          <h2 className="font-medium text-warm-dark mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5" /> Shipping Details
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {tracking.tracking_number && (
              <div>
                <p className="text-sm text-warm-muted">Tracking Number</p>
                <p className="font-mono font-medium">{tracking.tracking_number}</p>
              </div>
            )}
            {tracking.carrier && (
              <div>
                <p className="text-sm text-warm-muted">Carrier</p>
                <p className="font-medium">{tracking.carrier}</p>
              </div>
            )}
            {tracking.estimated_delivery && (
              <div>
                <p className="text-sm text-warm-muted">Estimated Delivery</p>
                <p className="font-medium">{tracking.estimated_delivery}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status History */}
      {tracking.status_history?.length > 0 && (
        <div className="bg-white border border-warm rounded-xl p-6 mb-8">
          <h2 className="font-medium text-warm-dark mb-4">Status History</h2>
          <div className="space-y-4">
            {tracking.status_history.slice().reverse().map((entry, index) => (
              <div key={index} className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-accent-terracotta mt-2" />
                <div>
                  <p className="font-medium text-warm-dark">{entry.status?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
                  <p className="text-sm text-warm-muted">{new Date(entry.timestamp).toLocaleString('en-GB')}</p>
                  {entry.note && <p className="text-sm text-warm-muted mt-1">{entry.note}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order Summary */}
      <div className="bg-white border border-warm rounded-xl p-6">
        <h2 className="font-medium text-warm-dark mb-4">Order Summary</h2>
        <div className="space-y-4 mb-6">
          {tracking.items?.map((item, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-16 h-16 bg-warm-subtle rounded overflow-hidden flex-shrink-0">
                {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm line-clamp-2">{item.name}</p>
                <p className="text-warm-muted text-sm">Qty: {item.quantity}</p>
              </div>
              <p className="font-medium">£{(item.price * item.quantity).toFixed(2)}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-warm pt-4 flex justify-between">
          <span className="font-serif text-lg">Total</span>
          <span className="font-serif text-lg">£{tracking.total?.toFixed(2)}</span>
        </div>
      </div>

      {/* Delivery Address */}
      <div className="bg-warm-paper rounded-xl p-6 mt-8">
        <h2 className="font-medium text-warm-dark mb-4">Delivery Address</h2>
        <p className="text-warm-muted">
          {tracking.shipping_address?.name}<br />
          {tracking.shipping_address?.line1}<br />
          {tracking.shipping_address?.line2 && <>{tracking.shipping_address.line2}<br /></>}
          {tracking.shipping_address?.city}, {tracking.shipping_address?.postcode}
        </p>
      </div>
    </div>
  );
};

// Wishlist Page
const WishlistPage = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login?redirect=/wishlist");
      return;
    }
    axios.get(`${API}/wishlist`).then(res => setProducts(res.data.products));
  }, [user, navigate]);

  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/remove/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
      toast.success("Removed from wishlist");
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="wishlist-page">
      <h1 className="font-serif text-3xl mb-8">My Wishlist</h1>
      {products.length === 0 ? (
        <div className="text-center py-16">
          <Heart className="w-12 h-12 mx-auto text-warm-subtle mb-4" />
          <p className="text-warm-muted">Your wishlist is empty</p>
          <Link to="/shop" className="text-accent-terracotta mt-2 inline-block">Browse Products</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {products.map((product) => (
            <div key={product.id} className="relative">
              <ProductCard product={product} />
              <button 
                onClick={() => removeFromWishlist(product.id)}
                className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-red-50"
              >
                <X className="w-4 h-4 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { user } = useAuth();
  const { cart, loading: cartLoading } = useCart();
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState({ name: "", line1: "", line2: "", city: "", postcode: "", country: "UK" });
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login?redirect=/checkout");
      return;
    }
    // Only redirect if cart is empty AND cart has finished loading
    if (!cartLoading && cart.items.length === 0) {
      navigate("/shop");
    }
  }, [user, cart.items.length, cartLoading, navigate]);

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    setApplyingDiscount(true);
    try {
      const res = await axios.get(`${API}/discount/validate/${discountCode}`);
      if (res.data.min_order_amount > cart.total) {
        toast.error(`Minimum order amount for this code is £${res.data.min_order_amount}`);
        setApplyingDiscount(false);
        return;
      }
      setDiscount(res.data);
      toast.success(`Discount code "${res.data.code}" applied!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid discount code");
      setDiscount(null);
    }
    setApplyingDiscount(false);
  };

  const removeDiscount = () => {
    setDiscount(null);
    setDiscountCode("");
  };

  const calculateDiscount = () => {
    if (!discount) return 0;
    if (discount.discount_type === "percentage") {
      return cart.total * (discount.discount_value / 100);
    }
    return Math.min(discount.discount_value, cart.total);
  };

  const discountAmount = calculateDiscount();
  const shipping = cart.total >= 500 ? 0 : 49.99;
  const finalTotal = cart.total - discountAmount + shipping;

  const handleCheckout = async () => {
    if (!address.name || !address.line1 || !address.city || !address.postcode) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/create-session`, {
        shipping_address: address,
        origin_url: window.location.origin,
        discount_code: discount?.code || null
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create checkout session");
      setLoading(false);
    }
  };

  if (!user || cart.items.length === 0) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="checkout-page">
      <h1 className="font-serif text-3xl mb-8">Checkout</h1>
      
      <div className="grid md:grid-cols-2 gap-12">
        <div>
          <h2 className="font-serif text-xl mb-6">Shipping Address</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">Full Name *</label>
              <Input value={address.name} onChange={(e) => setAddress({...address, name: e.target.value})} data-testid="checkout-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">Address Line 1 *</label>
              <Input value={address.line1} onChange={(e) => setAddress({...address, line1: e.target.value})} data-testid="checkout-line1" />
            </div>
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">Address Line 2</label>
              <Input value={address.line2} onChange={(e) => setAddress({...address, line2: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-warm-muted block mb-2">City *</label>
                <Input value={address.city} onChange={(e) => setAddress({...address, city: e.target.value})} data-testid="checkout-city" />
              </div>
              <div>
                <label className="text-sm font-medium text-warm-muted block mb-2">Postcode *</label>
                <Input value={address.postcode} onChange={(e) => setAddress({...address, postcode: e.target.value})} data-testid="checkout-postcode" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-serif text-xl mb-6">Order Summary</h2>
          <div className="bg-warm-paper p-6 rounded-lg">
            <div className="space-y-4 mb-6">
              {cart.items.map((item) => (
                <div key={item.product_id} className="flex gap-4">
                  <div className="w-16 h-16 bg-warm-subtle rounded overflow-hidden">
                    {item.product?.images?.[0] && <img src={item.product.images[0]} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.product?.name}</p>
                    <p className="text-warm-muted text-sm">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-medium">£{item.item_total?.toFixed(2)}</p>
                </div>
              ))}
            </div>

            {/* Discount Code */}
            <div className="border-t border-warm pt-4 mb-4">
              <label className="text-sm font-medium text-warm-muted block mb-2">
                <Tag className="w-4 h-4 inline mr-1" />
                Discount Code
              </label>
              {discount ? (
                <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                  <div>
                    <span className="font-medium text-green-700">{discount.code}</span>
                    <span className="text-green-600 text-sm ml-2">
                      -{discount.discount_type === "percentage" ? `${discount.discount_value}%` : `£${discount.discount_value}`}
                    </span>
                  </div>
                  <button onClick={removeDiscount} className="text-red-500 hover:text-red-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input 
                    value={discountCode} 
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="flex-1"
                    data-testid="discount-input"
                  />
                  <Button 
                    onClick={applyDiscount} 
                    variant="outline"
                    disabled={applyingDiscount || !discountCode.trim()}
                    data-testid="apply-discount-btn"
                  >
                    {applyingDiscount ? "..." : "Apply"}
                  </Button>
                </div>
              )}
              <p className="text-xs text-warm-muted mt-2">Try: WELCOME10, SAVE20, FLAT50</p>
            </div>

            <div className="border-t border-warm pt-4 space-y-2">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>£{cart.total.toFixed(2)}</span>
              </div>
              {discount && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({discount.code})</span>
                  <span>-£{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>{cart.total >= 500 ? "Free" : "£49.99"}</span>
              </div>
              <div className="flex justify-between font-serif text-xl pt-2 border-t border-warm">
                <span>Total</span>
                <span>£{finalTotal.toFixed(2)}</span>
              </div>
            </div>
            <Button 
              className="w-full btn-primary mt-6" 
              onClick={handleCheckout}
              disabled={loading}
              data-testid="pay-btn"
            >
              {loading ? "Processing..." : "Pay with Stripe"}
            </Button>
            <p className="text-xs text-center text-warm-muted mt-4">
              <CreditCard className="w-4 h-4 inline mr-1" />
              Secure payment powered by Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Checkout Success
const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const { fetchCart } = useCart();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          fetchCart();
        } else if (res.data.status === "expired") {
          setStatus("expired");
        } else {
          setTimeout(checkStatus, 2000);
        }
      } catch (e) {
        setStatus("error");
      }
    };
    checkStatus();
  }, [sessionId, fetchCart]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="checkout-success">
      <div className="text-center max-w-md">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 border-4 border-warm-subtle border-t-accent-terracotta rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-serif text-2xl mb-2">Processing Payment...</h1>
            <p className="text-warm-muted">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="font-serif text-2xl mb-2">Order Confirmed!</h1>
            <p className="text-warm-muted mb-6">Thank you for your purchase. We'll send you an email with your order details.</p>
            <Link to="/account" className="btn-primary inline-block">View Orders</Link>
          </>
        )}
        {status === "error" && (
          <>
            <X className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-serif text-2xl mb-2">Something went wrong</h1>
            <p className="text-warm-muted mb-6">Please contact support if you were charged.</p>
            <Link to="/shop" className="btn-primary inline-block">Continue Shopping</Link>
          </>
        )}
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminLayout = ({ children }) => {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user?.is_admin) navigate("/login");
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-layout-loading-state">
        <Loader2 className="w-8 h-8 animate-spin text-warm-muted" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  const navSections = [
    {
      title: "Overview",
      items: [
        { path: "/admin", icon: BarChart3, label: "Dashboard" },
      ]
    },
    {
      title: "Sales",
      items: [
        { path: "/admin/orders", icon: Package, label: "Orders" },
        { path: "/admin/customers", icon: Users, label: "Customers" },
      ]
    },
    {
      title: "Catalog",
      items: [
        { path: "/admin/products", icon: Grid3X3, label: "Products" },
        { path: "/admin/profitability", icon: TrendingUp, label: "Profitability" },
        { path: "/admin/data-manager", icon: Database, label: "Data Manager" },
        { path: "/admin/import-manager", icon: Upload, label: "Import Manager" },
        { path: "/admin/tad-import", icon: Download, label: "TAD Import" },
        { path: "/admin/database", icon: Database, label: "Database Migration" },
        { path: "/admin/product-tools", icon: Zap, label: "Product Tools" },
      ]
    },
    {
      title: "Marketing",
      items: [
        { path: "/admin/discounts", icon: Percent, label: "Discount Codes" },
        { path: "/admin/seo-audit", icon: Activity, label: "SEO" },
      ]
    },
    {
      title: "Settings",
      items: [
        { path: "/admin/navigation", icon: Navigation, label: "Navigation" },
        { path: "/admin/settings", icon: Settings, label: "Settings" },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-warm-paper">
      <div className="admin-sidebar">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="font-serif text-xl">Voco Living</Link>
          <p className="text-white/50 text-sm">Admin Panel</p>
        </div>
        <nav className="py-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {navSections.map((section) => (
            <div key={section.title} className="mb-4">
              <p className="px-6 py-2 text-xs uppercase tracking-wider text-white/40">{section.title}</p>
              {section.items.map((item) => (
                <Link 
                  key={item.path}
                  to={item.path}
                  className={`admin-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-warm-dark">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-accent-terracotta flex items-center justify-center text-white text-sm font-medium">
              {user?.first_name?.[0] || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={logout} className="admin-nav-item w-full">
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </div>
      <div className="ml-64 p-8">{children}</div>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [seoSummary, setSeoSummary] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`).then(res => setStats(res.data));
  }, []);

  useEffect(() => {
    const fetchSeoSummary = async (forceRefresh = false) => {
      try {
        const response = await axios.get(`${API}/admin/seo/audit`, {
          params: {
            force_refresh: forceRefresh,
            origin: window.location.origin,
          },
        });
        setSeoSummary(response.data?.summary || null);
      } catch (error) {
        console.error("Failed to fetch SEO summary", error);
      }
    };

    fetchSeoSummary(false);
    const interval = setInterval(() => fetchSeoSummary(false), 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!stats) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-warm-muted" />
    </div>
  );

  const quickActions = [
    { label: "Import Manager", icon: Upload, path: "/admin/import-manager", color: "bg-blue-500" },
    { label: "Manage Orders", icon: Package, path: "/admin/orders", color: "bg-green-500" },
    { label: "Create Discount", icon: Percent, path: "/admin/discounts", color: "bg-purple-500" },
    { label: "Product Tools", icon: Zap, path: "/admin/product-tools", color: "bg-orange-500" },
    { label: "SEO", icon: Activity, path: "/admin/seo-audit", color: "bg-rose-500" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl">Dashboard</h1>
          <p className="text-warm-muted">Welcome back! Here's what's happening with your store.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.open('/', '_blank')}>
            <Eye className="w-4 h-4 mr-2" />
            View Store
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-warm border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warm-muted text-sm uppercase tracking-wider">Total Revenue</p>
              <p className="font-serif text-3xl mt-2">£{stats.total_revenue.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-warm border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warm-muted text-sm uppercase tracking-wider">Orders</p>
              <p className="font-serif text-3xl mt-2">{stats.total_orders}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-warm border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warm-muted text-sm uppercase tracking-wider">Products</p>
              <p className="font-serif text-3xl mt-2">{stats.total_products}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Grid3X3 className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-warm border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warm-muted text-sm uppercase tracking-wider">Customers</p>
              <p className="font-serif text-3xl mt-2">{stats.total_customers}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-warm border-l-4 border-rose-500" data-testid="admin-dashboard-seo-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-warm-muted text-sm uppercase tracking-wider">SEO</p>
              <p className="font-serif text-3xl mt-2" data-testid="admin-dashboard-seo-issue-count">
                {seoSummary?.total_issues ?? stats.seo_summary?.total_issues ?? "-"}
              </p>
              <p className="text-xs text-warm-muted mt-1">Issues detected</p>
            </div>
            <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
              <Activity className="w-6 h-6 text-rose-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-warm p-6 mb-8">
        <h2 className="font-serif text-xl mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {quickActions.map((action) => (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              className="flex items-center gap-3 p-4 rounded-lg border border-warm hover:border-warm-dark hover:bg-warm-paper transition-colors text-left"
              data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center`}>
                <action.icon className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-warm-dark">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow-warm">
        <div className="p-6 border-b border-warm flex items-center justify-between">
          <h2 className="font-serif text-xl">Recent Orders</h2>
          <Link to="/admin/orders" className="text-sm text-accent-terracotta hover:underline">
            View All
          </Link>
        </div>
        {stats.recent_orders.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 text-warm-subtle mx-auto mb-4" />
            <p className="text-warm-muted">No orders yet</p>
          </div>
        ) : (
          <table className="w-full admin-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_orders.map((order) => (
                <tr key={order.id}>
                  <td className="font-mono text-sm">{order.id.slice(0, 8)}</td>
                  <td>{order.customer?.first_name} {order.customer?.last_name}</td>
                  <td className="font-medium">£{order.total.toFixed(2)}</td>
                  <td>
                    <Badge variant={order.status === "delivered" ? "default" : "secondary"}>
                      {order.status}
                    </Badge>
                  </td>
                  <td className="text-warm-muted">{new Date(order.created_at).toLocaleDateString()}</td>
                  <td>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/orders`)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');

  const fetchOrders = async () => {
    try {
      let url = `${API}/admin/orders?page=${page}&limit=20`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await axios.get(url);
      setOrders(res.data.orders);
      setTotal(res.data.total);
    } catch (e) {
      toast.error("Failed to load orders");
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders/stats`);
      setStats(res.data);
    } catch (e) {}
  };

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [page, statusFilter, search]);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}?status=${status}`);
      setOrders(orders.map(o => o.id === orderId ? {...o, status} : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({...selectedOrder, status});
      }
      toast.success("Order status updated");
      fetchStats();
    } catch (e) {
      toast.error("Failed to update order");
    }
  };

  const updateTracking = async (orderId) => {
    if (!trackingNumber.trim()) return;
    try {
      await axios.put(`${API}/admin/orders/${orderId}/tracking`, { tracking_number: trackingNumber });
      setOrders(orders.map(o => o.id === orderId ? {...o, tracking_number: trackingNumber} : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({...selectedOrder, tracking_number: trackingNumber});
      }
      setTrackingNumber('');
      toast.success("Tracking number updated");
    } catch (e) {
      toast.error("Failed to update tracking");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      shipped: 'bg-purple-100 text-purple-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl">Orders</h1>
          <p className="text-warm-muted">{total} total orders</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <button onClick={() => setStatusFilter('all')} className={`p-4 rounded-lg text-left transition-colors ${statusFilter === 'all' ? 'bg-warm-dark text-white' : 'bg-white border border-warm'}`}>
            <p className="text-sm opacity-70">All Orders</p>
            <p className="text-2xl font-serif">{stats.total || 0}</p>
          </button>
          <button onClick={() => setStatusFilter('pending')} className={`p-4 rounded-lg text-left transition-colors ${statusFilter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
            <p className="text-sm opacity-70">Pending</p>
            <p className="text-2xl font-serif">{stats.pending || 0}</p>
          </button>
          <button onClick={() => setStatusFilter('processing')} className={`p-4 rounded-lg text-left transition-colors ${statusFilter === 'processing' ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
            <p className="text-sm opacity-70">Processing</p>
            <p className="text-2xl font-serif">{stats.processing || 0}</p>
          </button>
          <button onClick={() => setStatusFilter('shipped')} className={`p-4 rounded-lg text-left transition-colors ${statusFilter === 'shipped' ? 'bg-purple-500 text-white' : 'bg-purple-50 text-purple-700 border border-purple-200'}`}>
            <p className="text-sm opacity-70">Shipped</p>
            <p className="text-2xl font-serif">{stats.shipped || 0}</p>
          </button>
          <button onClick={() => setStatusFilter('delivered')} className={`p-4 rounded-lg text-left transition-colors ${statusFilter === 'delivered' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            <p className="text-sm opacity-70">Delivered</p>
            <p className="text-2xl font-serif">{stats.delivered || 0}</p>
          </button>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow-warm p-4 mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-muted" />
            <Input
              placeholder="Search by order ID, customer name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="order-search"
            />
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-warm overflow-hidden">
        {orders.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 text-warm-subtle mx-auto mb-4" />
            <p className="text-warm-muted">No orders found</p>
          </div>
        ) : (
          <table className="w-full admin-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
                <th>Tracking</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="font-mono text-sm">{order.id.slice(0, 8)}...</td>
                  <td>
                    <p className="font-medium">{order.customer?.first_name} {order.customer?.last_name}</p>
                    <p className="text-xs text-warm-muted">{order.customer?.email}</p>
                  </td>
                  <td>{order.items?.length || 0} items</td>
                  <td className="font-medium">£{(order.total || 0).toFixed(2)}</td>
                  <td>
                    <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v)}>
                      <SelectTrigger className={`w-32 ${getStatusColor(order.status)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="text-warm-muted text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
                  <td className="font-mono text-xs">{order.tracking_number || '-'}</td>
                  <td>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(order)} data-testid={`view-order-${order.id.slice(0,8)}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-4 py-2">Page {page} of {Math.ceil(total / 20)}</span>
          <Button variant="outline" onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 20)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-lg w-full max-w-3xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl">Order #{selectedOrder.id.slice(0, 8)}</h2>
                <p className="text-sm text-warm-muted">{new Date(selectedOrder.created_at).toLocaleString()}</p>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-warm-paper rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Status & Actions */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="font-medium text-sm text-warm-muted mb-2">Order Status</h3>
                  <Select value={selectedOrder.status} onValueChange={(v) => updateStatus(selectedOrder.id, v)}>
                    <SelectTrigger className={`w-full ${getStatusColor(selectedOrder.status)}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-warm-muted mb-2">Tracking Number</h3>
                  <div className="flex gap-2">
                    <Input 
                      value={trackingNumber || selectedOrder.tracking_number || ''} 
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="Enter tracking number"
                    />
                    <Button onClick={() => updateTracking(selectedOrder.id)}>
                      <Truck className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="p-4 bg-warm-paper rounded-lg">
                  <h3 className="font-medium text-sm text-warm-muted mb-2">Customer</h3>
                  <p className="font-medium">{selectedOrder.customer?.first_name} {selectedOrder.customer?.last_name}</p>
                  <p className="text-sm">{selectedOrder.customer?.email}</p>
                  <p className="text-sm">{selectedOrder.customer?.phone}</p>
                </div>
                <div className="p-4 bg-warm-paper rounded-lg">
                  <h3 className="font-medium text-sm text-warm-muted mb-2">Shipping Address</h3>
                  <p className="text-sm">{selectedOrder.shipping_address?.address_line1}</p>
                  {selectedOrder.shipping_address?.address_line2 && (
                    <p className="text-sm">{selectedOrder.shipping_address?.address_line2}</p>
                  )}
                  <p className="text-sm">{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.postcode}</p>
                  <p className="text-sm">{selectedOrder.shipping_address?.country}</p>
                </div>
              </div>
              
              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-medium text-sm text-warm-muted mb-3">Order Items</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-warm-paper">
                      <tr>
                        <th className="p-3 text-left">Product</th>
                        <th className="p-3 text-right">Price</th>
                        <th className="p-3 text-right">Qty</th>
                        <th className="p-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((item, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              {item.image && (
                                <img src={item.image} alt="" className="w-10 h-10 object-cover rounded" />
                              )}
                              <span>{item.name}</span>
                            </div>
                          </td>
                          <td className="p-3 text-right">{typeof item.price === 'string' ? item.price : `£${item.price || 0}`}</td>
                          <td className="p-3 text-right">{item.quantity}</td>
                          <td className="p-3 text-right font-medium">
                            £{((parseFloat(String(item.price || 0).replace('£','').replace(',','')) || 0) * item.quantity).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Order Summary */}
              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-warm-muted">Subtotal</span>
                      <span>£{(selectedOrder.subtotal || selectedOrder.total || 0).toFixed(2)}</span>
                    </div>
                    {selectedOrder.discount_amount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount ({selectedOrder.discount_code})</span>
                        <span>-£{selectedOrder.discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-warm-muted">Shipping</span>
                      <span>£{(selectedOrder.shipping || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-medium text-lg border-t pt-2">
                      <span>Total</span>
                      <span>£{(selectedOrder.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(null);
  const [bulkValue, setBulkValue] = useState('');
  const [bulkAction, setBulkAction] = useState('set');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, active, inactive, low_stock, out_of_stock
  const [inventoryStats, setInventoryStats] = useState(null);
  const fileInputRef = useRef(null);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/products?page=${page}&limit=20`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const res = await axios.get(url);
      let filtered = res.data.products;
      
      // Apply frontend filters
      if (filter === 'active') filtered = filtered.filter(p => p.is_active !== false);
      if (filter === 'inactive') filtered = filtered.filter(p => p.is_active === false);
      if (filter === 'low_stock') filtered = filtered.filter(p => p.stock_quantity <= 5 && p.stock_quantity > 0);
      if (filter === 'out_of_stock') filtered = filtered.filter(p => !p.in_stock || p.stock_quantity <= 0);
      
      setProducts(filtered);
      setTotal(res.data.total);
    } catch (e) {
      toast.error("Failed to load products");
    }
    setLoading(false);
  };

  const fetchInventoryStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/products/inventory?limit=1`);
      setInventoryStats(res.data.stats);
    } catch (e) {}
  };

  useEffect(() => {
    fetchProducts();
    fetchInventoryStats();
  }, [page, search, filter]);

  const toggleSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const toggleProductStatus = async (productId, field, currentValue) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/status?${field}=${!currentValue}`);
      setProducts(products.map(p => p.id === productId ? {...p, [field]: !currentValue} : p));
      toast.success("Product updated");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
      toast.success("Product deleted");
    } catch (e) {
      toast.error("Failed to delete product");
    }
  };

  const bulkUpdatePrices = async () => {
    if (!selectedProducts.length || !bulkValue) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/products/bulk/prices`, {
        product_ids: selectedProducts,
        action: bulkAction,
        value: parseFloat(bulkValue)
      });
      toast.success(`Updated prices for ${selectedProducts.length} products`);
      setShowBulkModal(null);
      setSelectedProducts([]);
      fetchProducts();
    } catch (e) {
      toast.error("Failed to update prices");
    }
    setLoading(false);
  };

  const bulkUpdateInventory = async () => {
    if (!selectedProducts.length || bulkValue === '') return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/products/bulk/inventory`, {
        product_ids: selectedProducts,
        action: bulkAction,
        quantity: parseInt(bulkValue)
      });
      toast.success(`Updated inventory for ${selectedProducts.length} products`);
      setShowBulkModal(null);
      setSelectedProducts([]);
      fetchProducts();
      fetchInventoryStats();
    } catch (e) {
      toast.error("Failed to update inventory");
    }
    setLoading(false);
  };

  const bulkUpdateStatus = async (statusField, value) => {
    if (!selectedProducts.length) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/products/bulk/status`, {
        product_ids: selectedProducts,
        [statusField]: value
      });
      toast.success(`Updated ${selectedProducts.length} products`);
      setSelectedProducts([]);
      fetchProducts();
    } catch (e) {
      toast.error("Failed to update status");
    }
    setLoading(false);
  };

  const bulkDelete = async () => {
    if (!selectedProducts.length) return;
    if (!window.confirm(`Delete ${selectedProducts.length} products? This cannot be undone.`)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/products/bulk/delete`, selectedProducts);
      toast.success(`Deleted ${selectedProducts.length} products`);
      setSelectedProducts([]);
      fetchProducts();
      fetchInventoryStats();
    } catch (e) {
      toast.error("Failed to delete products");
    }
    setLoading(false);
  };

  const saveProduct = async (productData) => {
    setLoading(true);
    try {
      if (editingProduct.id) {
        await axios.put(`${API}/admin/products/${editingProduct.id}/full`, productData);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products/create`, productData);
        toast.success("Product created");
      }
      setEditingProduct(null);
      fetchProducts();
      fetchInventoryStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save product");
    }
    setLoading(false);
  };

  const uploadImage = async (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/products/${productId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Image uploaded");
      return res.data.image_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to upload image");
      return null;
    }
  };

  const deleteImage = async (productId, imageIndex) => {
    try {
      await axios.delete(`${API}/admin/products/${productId}/images/${imageIndex}`);
      toast.success("Image deleted");
      return true;
    } catch (e) {
      toast.error("Failed to delete image");
      return false;
    }
  };

  return (
    <div data-testid="admin-products">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl">Products</h1>
          <p className="text-warm-muted">{total} total products</p>
        </div>
        <Button onClick={() => setEditingProduct({ id: null })} data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Inventory Stats */}
      {inventoryStats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <button 
            onClick={() => setFilter('all')}
            className={`p-4 rounded-lg border-2 text-left transition-colors ${filter === 'all' ? 'border-warm-dark bg-white' : 'border-transparent bg-white'}`}
          >
            <p className="text-sm text-warm-muted">Total Products</p>
            <p className="text-2xl font-serif">{inventoryStats.total_products}</p>
          </button>
          <button 
            onClick={() => setFilter('active')}
            className={`p-4 rounded-lg border-2 text-left transition-colors ${filter === 'active' ? 'border-green-500 bg-green-50' : 'border-transparent bg-white'}`}
          >
            <p className="text-sm text-green-600">In Stock</p>
            <p className="text-2xl font-serif text-green-700">{inventoryStats.in_stock}</p>
          </button>
          <button 
            onClick={() => setFilter('low_stock')}
            className={`p-4 rounded-lg border-2 text-left transition-colors ${filter === 'low_stock' ? 'border-yellow-500 bg-yellow-50' : 'border-transparent bg-white'}`}
          >
            <p className="text-sm text-yellow-600">Low Stock</p>
            <p className="text-2xl font-serif text-yellow-700">{inventoryStats.low_stock}</p>
          </button>
          <button 
            onClick={() => setFilter('out_of_stock')}
            className={`p-4 rounded-lg border-2 text-left transition-colors ${filter === 'out_of_stock' ? 'border-red-500 bg-red-50' : 'border-transparent bg-white'}`}
          >
            <p className="text-sm text-red-600">Out of Stock</p>
            <p className="text-2xl font-serif text-red-700">{inventoryStats.out_of_stock}</p>
          </button>
        </div>
      )}

      {/* Search and Bulk Actions */}
      <div className="bg-white rounded-lg shadow-warm p-4 mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-muted" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="product-search"
            />
          </div>
          
          {selectedProducts.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-warm-muted">{selectedProducts.length} selected</span>
              <Button variant="outline" size="sm" onClick={() => setShowBulkModal('price')}>
                <DollarSign className="w-4 h-4 mr-1" />
                Prices
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowBulkModal('inventory')}>
                <Package className="w-4 h-4 mr-1" />
                Inventory
              </Button>
              <Button variant="outline" size="sm" onClick={() => bulkUpdateStatus('is_active', true)}>
                <Check className="w-4 h-4 mr-1" />
                Activate
              </Button>
              <Button variant="outline" size="sm" onClick={() => bulkUpdateStatus('is_active', false)}>
                <X className="w-4 h-4 mr-1" />
                Deactivate
              </Button>
              <Button variant="outline" size="sm" className="text-red-500" onClick={bulkDelete}>
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow-warm overflow-hidden">
        <table className="w-full admin-table">
          <thead>
            <tr>
              <th className="w-12">
                <input 
                  type="checkbox" 
                  checked={selectedProducts.length === products.length && products.length > 0}
                  onChange={selectAll}
                  className="rounded"
                />
              </th>
              <th>Product</th>
              <th>SKU</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-warm-muted" />
              </td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-8 text-warm-muted">No products found</td></tr>
            ) : products.map((product) => (
              <tr key={product.id} className={product.is_active === false ? 'opacity-50' : ''}>
                <td>
                  <input 
                    type="checkbox" 
                    checked={selectedProducts.includes(product.id)}
                    onChange={() => toggleSelection(product.id)}
                    className="rounded"
                  />
                </td>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-warm-subtle rounded overflow-hidden flex-shrink-0">
                      {product.images?.[0] && (
                        <img 
                          src={product.images[0].split(',')[0]} 
                          alt="" 
                          className="w-full h-full object-cover" 
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate max-w-xs">{product.name}</p>
                      <p className="text-xs text-warm-muted">{product.collection_name}</p>
                    </div>
                  </div>
                </td>
                <td className="font-mono text-sm">{product.sku || '-'}</td>
                <td className="font-medium">{product.price}</td>
                <td>
                  <span className={`text-sm ${product.stock_quantity <= 5 ? 'text-red-600 font-medium' : ''}`}>
                    {product.stock_quantity ?? 10}
                  </span>
                </td>
                <td>
                  <div className="flex gap-1">
                    <Badge 
                      variant={product.is_active !== false ? "default" : "secondary"}
                      className="cursor-pointer"
                      onClick={() => toggleProductStatus(product.id, 'is_active', product.is_active !== false)}
                    >
                      {product.is_active !== false ? "Active" : "Inactive"}
                    </Badge>
                    {product.on_sale && <Badge variant="outline">Sale</Badge>}
                  </div>
                </td>
                <td>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditingProduct(product)} data-testid={`edit-${product.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteProduct(product.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center gap-2 mt-6">
        <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="px-4 py-2">Page {page}</span>
        <Button variant="outline" onClick={() => setPage(p => p + 1)} disabled={products.length < 20}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Product Edit Modal */}
      {editingProduct && (
        <ProductEditModal
          product={editingProduct}
          onSave={saveProduct}
          onClose={() => setEditingProduct(null)}
          onUploadImage={uploadImage}
          onDeleteImage={deleteImage}
          loading={loading}
        />
      )}

      {/* Bulk Price Modal */}
      {showBulkModal === 'price' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="font-serif text-xl mb-4">Bulk Update Prices</h2>
            <p className="text-sm text-warm-muted mb-4">{selectedProducts.length} products selected</p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-2">Action</label>
                <select 
                  value={bulkAction} 
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="set">Set price to</option>
                  <option value="increase_percent">Increase by %</option>
                  <option value="decrease_percent">Decrease by %</option>
                  <option value="increase_amount">Increase by £</option>
                  <option value="decrease_amount">Decrease by £</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">
                  {bulkAction.includes('percent') ? 'Percentage' : 'Amount (£)'}
                </label>
                <Input 
                  type="number" 
                  value={bulkValue} 
                  onChange={(e) => setBulkValue(e.target.value)}
                  placeholder={bulkAction.includes('percent') ? 'e.g., 10' : 'e.g., 5.00'}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowBulkModal(null)}>Cancel</Button>
              <Button onClick={bulkUpdatePrices} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update Prices
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Inventory Modal */}
      {showBulkModal === 'inventory' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="font-serif text-xl mb-4">Bulk Update Inventory</h2>
            <p className="text-sm text-warm-muted mb-4">{selectedProducts.length} products selected</p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-2">Action</label>
                <select 
                  value={bulkAction} 
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="set">Set quantity to</option>
                  <option value="add">Add to quantity</option>
                  <option value="subtract">Subtract from quantity</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Quantity</label>
                <Input 
                  type="number" 
                  min="0"
                  value={bulkValue} 
                  onChange={(e) => setBulkValue(e.target.value)}
                  placeholder="e.g., 10"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowBulkModal(null)}>Cancel</Button>
              <Button onClick={bulkUpdateInventory} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update Inventory
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Product Edit Modal Component
const ProductEditModal = ({ product, onSave, onClose, onUploadImage, onDeleteImage, loading }) => {
  const [formData, setFormData] = useState({
    name: product.name || '',
    price: product.price?.replace('Now ', '') || '',
    rrp_price: product.rrp_price || '',
    description: product.description || '',
    short_description: product.short_description || '',
    collection_name: product.collection_name || '',
    category: product.category || '',
    product_type: product.product_type || '',
    sku: product.sku || '',
    size_cm: product.size_cm || '',
    assembled_weight_kg: product.assembled_weight_kg || '',
    ean_code: product.ean_code || '',
    stock_quantity: product.stock_quantity ?? 10,
    in_stock: product.in_stock !== false,
    is_active: product.is_active !== false,
    on_sale: product.on_sale || false,
    images: product.images || []
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !product.id) return;
    
    setUploading(true);
    const imageUrl = await onUploadImage(product.id, file);
    if (imageUrl) {
      setFormData(prev => ({ ...prev, images: [...prev.images, imageUrl] }));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteImage = async (index) => {
    if (!product.id) {
      // For new products, just remove from local state
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
      return;
    }
    
    const success = await onDeleteImage(product.id, index);
    if (success) {
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="font-serif text-xl">{product.id ? 'Edit Product' : 'Add New Product'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-warm-paper rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Basic Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-warm-dark border-b pb-2">Basic Information</h3>
              
              <div>
                <label className="text-sm font-medium text-warm-muted block mb-1">Product Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  data-testid="product-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">Price</label>
                  <Input
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    placeholder="£99.99"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">RRP Price</label>
                  <Input
                    value={formData.rrp_price}
                    onChange={(e) => handleChange('rrp_price', e.target.value)}
                    placeholder="£129.99"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">Collection</label>
                  <Input
                    value={formData.collection_name}
                    onChange={(e) => handleChange('collection_name', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">Category</label>
                  <Input
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-warm-muted block mb-1">Short Description</label>
                <Input
                  value={formData.short_description}
                  onChange={(e) => handleChange('short_description', e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-warm-muted block mb-1">Full Description (HTML supported)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full p-2 border rounded min-h-[100px] text-sm"
                />
              </div>
            </div>
            
            {/* Right Column - Details & Inventory */}
            <div className="space-y-4">
              <h3 className="font-medium text-warm-dark border-b pb-2">Product Details</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">SKU</label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => handleChange('sku', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">EAN Code</label>
                  <Input
                    value={formData.ean_code}
                    onChange={(e) => handleChange('ean_code', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">Dimensions (cm)</label>
                  <Input
                    value={formData.size_cm}
                    onChange={(e) => handleChange('size_cm', e.target.value)}
                    placeholder="W x H x D"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">Weight (kg)</label>
                  <Input
                    value={formData.assembled_weight_kg}
                    onChange={(e) => handleChange('assembled_weight_kg', e.target.value)}
                  />
                </div>
              </div>
              
              <h3 className="font-medium text-warm-dark border-b pb-2 pt-4">Inventory & Status</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">Stock Quantity</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.stock_quantity}
                    onChange={(e) => handleChange('stock_quantity', parseInt(e.target.value) || 0)}
                    data-testid="stock-quantity-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-warm-muted block mb-1">Product Type</label>
                  <Input
                    value={formData.product_type}
                    onChange={(e) => handleChange('product_type', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.in_stock}
                    onChange={(e) => handleChange('in_stock', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">In Stock</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.on_sale}
                    onChange={(e) => handleChange('on_sale', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">On Sale</span>
                </label>
              </div>
              
              {/* Images Section */}
              <h3 className="font-medium text-warm-dark border-b pb-2 pt-4">Product Images</h3>
              
              <div className="grid grid-cols-4 gap-2">
                {formData.images.slice(0, 8).map((img, index) => (
                  <div key={index} className="relative aspect-square bg-warm-subtle rounded overflow-hidden group">
                    <img 
                      src={img.split(',')[0]} 
                      alt="" 
                      className="w-full h-full object-cover" 
                    />
                    <button
                      type="button"
                      onClick={() => handleDeleteImage(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                {product.id && (
                  <label className="aspect-square border-2 border-dashed border-warm rounded flex items-center justify-center cursor-pointer hover:bg-warm-paper transition-colors">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    {uploading ? (
                      <Loader2 className="w-6 h-6 animate-spin text-warm-muted" />
                    ) : (
                      <Plus className="w-6 h-6 text-warm-muted" />
                    )}
                  </label>
                )}
              </div>
              {!product.id && (
                <p className="text-xs text-warm-muted">Save the product first to upload images</p>
              )}
            </div>
          </div>
          
          {/* Footer Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="save-product-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {product.id ? 'Save Changes' : 'Create Product'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/customers`).then(res => {
      setCustomers(res.data.customers);
      setTotal(res.data.total);
    });
  }, []);

  return (
    <div data-testid="admin-customers">
      <h1 className="font-serif text-3xl mb-8">Customers ({total})</h1>
      <div className="bg-white rounded-lg shadow-warm">
        <table className="w-full admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Orders</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer.id}>
                <td>{customer.first_name} {customer.last_name}</td>
                <td>{customer.email}</td>
                <td>{customer.order_count}</td>
                <td className="text-warm-muted">{new Date(customer.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Admin Import Products Component
const AdminImportProducts = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [importMode, setImportMode] = useState('all'); // all, new_only, update_only
  const [matchField, setMatchField] = useState('sku'); // sku, name, name_collection
  const [columnMapping, setColumnMapping] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [activeTab, setActiveTab] = useState('products'); // products, images
  const [imageSkuColumn, setImageSkuColumn] = useState('');
  const [imageImagesColumn, setImageImagesColumn] = useState('');
  const [replaceImages, setReplaceImages] = useState(false);
  const fileInputRef = useRef(null);

  const DB_FIELDS = [
    { key: 'name', label: 'Product Name', required: true },
    { key: 'price', label: 'Price' },
    { key: 'rrp_price', label: 'RRP Price' },
    { key: 'sku', label: 'SKU' },
    { key: 'collection_name', label: 'Collection' },
    { key: 'category', label: 'Category' },
    { key: 'product_type', label: 'Product Type' },
    { key: 'description', label: 'Description' },
    { key: 'short_description', label: 'Short Description' },
    { key: 'images', label: 'Images (pipe-separated)' },
    { key: 'size_cm', label: 'Dimensions' },
    { key: 'assembled_weight_kg', label: 'Weight (kg)' },
    { key: 'ean_code', label: 'EAN/Barcode' },
    { key: 'stock_quantity', label: 'Stock Quantity' },
    { key: 'in_stock', label: 'In Stock (true/false)' },
    { key: 'is_active', label: 'Active (true/false)' },
    { key: 'product_url', label: 'Product URL' },
  ];

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error("Please select a CSV file");
        return;
      }
      setFile(selectedFile);
      setPreview(null);
      setResult(null);
      setColumnMapping({});
      setSelectedRows([]);
      setSelectAll(true);
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/admin/products/import/preview-all`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(res.data);
      setColumnMapping(res.data.auto_mapping || {});
      // Auto-detect SKU and images columns for image import tab
      if (res.data.auto_mapping?.sku) setImageSkuColumn(res.data.auto_mapping.sku);
      if (res.data.auto_mapping?.images) setImageImagesColumn(res.data.auto_mapping.images);
      // Select all rows by default
      setSelectedRows(res.data.all_products.map((_, i) => i));
      toast.success(`CSV parsed: ${res.data.total_rows} rows found`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse CSV");
    }
    setLoading(false);
  };

  const handleImport = async () => {
    if (!file || !preview) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    const rowsToImport = selectAll ? null : selectedRows;
    
    try {
      const res = await axios.post(
        `${API}/admin/products/import/advanced`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          params: {
            column_mapping: JSON.stringify(columnMapping),
            selected_rows: rowsToImport ? JSON.stringify(rowsToImport) : '',
            import_mode: importMode,
            match_field: matchField
          }
        }
      );
      setResult(res.data);
      toast.success(`Import complete: ${res.data.imported} new, ${res.data.updated} updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import products");
    }
    setImporting(false);
  };

  const handleImportImagesOnly = async () => {
    if (!file || !imageSkuColumn || !imageImagesColumn) {
      toast.error("Please select SKU and Images columns");
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `${API}/admin/products/import/images-only`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: {
            sku_column: imageSkuColumn,
            images_column: imageImagesColumn,
            replace_existing: replaceImages
          }
        }
      );
      setResult({ ...res.data, isImageImport: true });
      toast.success(`Updated images for ${res.data.updated} products`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import images");
    }
    setImporting(false);
  };

  const toggleRowSelection = (rowIndex) => {
    setSelectedRows(prev => 
      prev.includes(rowIndex) 
        ? prev.filter(i => i !== rowIndex)
        : [...prev, rowIndex]
    );
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
      setSelectAll(false);
    } else {
      setSelectedRows(preview.all_products.map((_, i) => i));
      setSelectAll(true);
    }
  };

  const updateMapping = (dbField, csvColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [dbField]: csvColumn || undefined
    }));
  };

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setColumnMapping({});
    setSelectedRows([]);
    setSelectAll(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div data-testid="admin-import-products">
      <h1 className="font-serif text-3xl mb-2">Import Products</h1>
      <p className="text-warm-muted mb-6">Import products from CSV with custom field mapping</p>
      
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('products')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'products' ? 'bg-warm-dark text-white' : 'bg-white text-warm-dark border border-warm'}`}
        >
          <Upload className="w-4 h-4 inline mr-2" />
          Full Import
        </button>
        <button
          onClick={() => setActiveTab('images')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'images' ? 'bg-warm-dark text-white' : 'bg-white text-warm-dark border border-warm'}`}
        >
          <FileSpreadsheet className="w-4 h-4 inline mr-2" />
          Images Only
        </button>
      </div>
      
      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-warm p-6 mb-6">
        <h2 className="font-medium text-lg mb-4">Step 1: Select CSV File</h2>
        
        <div className="border-2 border-dashed border-warm rounded-lg p-8 text-center mb-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
            id="csv-upload"
            data-testid="csv-file-input"
          />
          <label htmlFor="csv-upload" className="cursor-pointer flex flex-col items-center">
            <FileSpreadsheet className="w-12 h-12 text-warm-muted mb-4" />
            <p className="text-warm-dark font-medium mb-2">
              {file ? file.name : "Click to select CSV file"}
            </p>
            <p className="text-sm text-warm-muted">Supports CSV files with product data</p>
          </label>
        </div>
        
        {file && (
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm text-warm-dark">
                <FileSpreadsheet className="w-4 h-4" />
                <span>{file.name}</span>
                <span className="text-warm-muted">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            </div>
            <Button onClick={handlePreview} disabled={loading} data-testid="preview-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              {loading ? 'Parsing...' : 'Preview'}
            </Button>
            <Button variant="outline" onClick={resetForm}>
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        )}
      </div>
      
      {/* Preview Section - Products Tab */}
      {preview && activeTab === 'products' && !result && (
        <>
          {/* Column Mapping */}
          <div className="bg-white rounded-lg shadow-warm p-6 mb-6">
            <h2 className="font-medium text-lg mb-4">Step 2: Map CSV Columns to Database Fields</h2>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              {DB_FIELDS.map(field => (
                <div key={field.key} className="flex items-center gap-2">
                  <label className="text-sm min-w-32">
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={columnMapping[field.key] || ''}
                    onChange={(e) => updateMapping(field.key, e.target.value)}
                    className="flex-1 p-2 text-sm border rounded"
                    data-testid={`mapping-${field.key}`}
                  >
                    <option value="">-- Not Mapped --</option>
                    {preview.csv_columns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            
            <div className="p-4 bg-warm-paper rounded-lg">
              <p className="text-sm text-warm-muted">
                <strong>{Object.values(columnMapping).filter(Boolean).length}</strong> of {DB_FIELDS.length} fields mapped. 
                Auto-detected mappings are pre-selected.
              </p>
            </div>
          </div>
          
          {/* Import Options */}
          <div className="bg-white rounded-lg shadow-warm p-6 mb-6">
            <h2 className="font-medium text-lg mb-4">Step 3: Import Options</h2>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium block mb-2">Import Mode</label>
                <select
                  value={importMode}
                  onChange={(e) => setImportMode(e.target.value)}
                  className="w-full p-2 border rounded"
                  data-testid="import-mode"
                >
                  <option value="all">Add new + Update existing</option>
                  <option value="new_only">Add new products only (skip existing)</option>
                  <option value="update_only">Update existing only (skip new)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Match Products By</label>
                <select
                  value={matchField}
                  onChange={(e) => setMatchField(e.target.value)}
                  className="w-full p-2 border rounded"
                  data-testid="match-field"
                >
                  <option value="sku">SKU</option>
                  <option value="name">Product Name</option>
                  <option value="name_collection">Name + Collection</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Row Selection */}
          <div className="bg-white rounded-lg shadow-warm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium text-lg">Step 4: Select Products to Import</h2>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="rounded"
                  />
                  <span className="text-sm">Select All ({preview.total_rows} rows)</span>
                </label>
                <Badge variant="secondary">
                  {selectAll ? preview.total_rows : selectedRows.length} selected
                </Badge>
              </div>
            </div>
            
            <div className="max-h-96 overflow-y-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-warm-paper sticky top-0">
                  <tr>
                    <th className="p-2 w-12 text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="p-2 text-left">Row</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">SKU</th>
                    <th className="p-2 text-left">Price</th>
                    <th className="p-2 text-left">Collection</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.all_products.map((product, i) => {
                    const nameCol = columnMapping.name;
                    const skuCol = columnMapping.sku;
                    const priceCol = columnMapping.price;
                    const collectionCol = columnMapping.collection_name;
                    return (
                      <tr 
                        key={i} 
                        className={`border-t hover:bg-warm-paper/50 ${(selectAll || selectedRows.includes(i)) ? '' : 'opacity-40'}`}
                      >
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selectAll || selectedRows.includes(i)}
                            onChange={() => toggleRowSelection(i)}
                            className="rounded"
                          />
                        </td>
                        <td className="p-2 text-warm-muted">{i + 1}</td>
                        <td className="p-2 max-w-xs truncate">{nameCol ? product[nameCol] : '-'}</td>
                        <td className="p-2 font-mono text-xs">{skuCol ? product[skuCol] : '-'}</td>
                        <td className="p-2">{priceCol ? product[priceCol] : '-'}</td>
                        <td className="p-2">{collectionCol ? product[collectionCol] : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {preview.total_rows > 500 && (
              <p className="text-sm text-warm-muted mt-2">
                Showing first 500 rows. All {preview.total_rows} rows will be processed during import.
              </p>
            )}
          </div>
          
          {/* Import Button */}
          <div className="bg-white rounded-lg shadow-warm p-6 mb-6">
            <h2 className="font-medium text-lg mb-4">Step 5: Import</h2>
            <div className="flex items-center gap-4">
              <Button 
                className="btn-primary"
                onClick={handleImport}
                disabled={importing || !columnMapping.name}
                data-testid="import-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import {selectAll ? preview.total_rows : selectedRows.length} Products
                  </>
                )}
              </Button>
              <p className="text-sm text-warm-muted">
                Mode: {importMode === 'all' ? 'Add & Update' : importMode === 'new_only' ? 'New Only' : 'Update Only'} 
                {' | '}Match by: {matchField === 'sku' ? 'SKU' : matchField === 'name' ? 'Name' : 'Name+Collection'}
              </p>
            </div>
            {!columnMapping.name && (
              <p className="text-sm text-red-500 mt-2">Please map the "Product Name" field to proceed.</p>
            )}
          </div>
        </>
      )}
      
      {/* Images Only Tab */}
      {preview && activeTab === 'images' && !result && (
        <div className="bg-white rounded-lg shadow-warm p-6 mb-6">
          <h2 className="font-medium text-lg mb-4">Update Images Only (by SKU)</h2>
          <p className="text-sm text-warm-muted mb-6">
            This will update product images for existing products matched by SKU. Products not found will be skipped.
          </p>
          
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-sm font-medium block mb-2">SKU Column *</label>
              <select
                value={imageSkuColumn}
                onChange={(e) => setImageSkuColumn(e.target.value)}
                className="w-full p-2 border rounded"
                data-testid="image-sku-column"
              >
                <option value="">-- Select Column --</option>
                {preview.csv_columns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Images Column *</label>
              <select
                value={imageImagesColumn}
                onChange={(e) => setImageImagesColumn(e.target.value)}
                className="w-full p-2 border rounded"
                data-testid="image-images-column"
              >
                <option value="">-- Select Column --</option>
                {preview.csv_columns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={replaceImages}
                onChange={(e) => setReplaceImages(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Replace existing images (otherwise append)</span>
            </label>
          </div>
          
          <Button 
            onClick={handleImportImagesOnly}
            disabled={importing || !imageSkuColumn || !imageImagesColumn}
            data-testid="import-images-btn"
          >
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating Images...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Update Images for {preview.total_rows} Products
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Result Section */}
      {result && (
        <div className="bg-white rounded-lg shadow-warm p-6">
          <h2 className="font-medium text-lg mb-4">
            {result.isImageImport ? 'Image Update Complete' : 'Import Complete'}
          </h2>
          
          <div className={`p-4 rounded-lg mb-6 ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-2">
              {result.success ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600" />
              )}
              <span className={`font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                {result.success ? 'Completed successfully!' : 'Completed with errors'}
              </span>
            </div>
          </div>
          
          <div className={`grid ${result.isImageImport ? 'grid-cols-3' : 'grid-cols-3'} gap-4 mb-6`}>
            {result.isImageImport ? (
              <>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Updated</p>
                  <p className="text-2xl font-serif text-green-700">{result.updated}</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-600">Not Found</p>
                  <p className="text-2xl font-serif text-yellow-700">{result.not_found}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Skipped</p>
                  <p className="text-2xl font-serif text-gray-700">{result.skipped}</p>
                </div>
              </>
            ) : (
              <>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Imported</p>
                  <p className="text-2xl font-serif text-green-700">{result.imported}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Updated</p>
                  <p className="text-2xl font-serif text-blue-700">{result.updated}</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-600">Skipped</p>
                  <p className="text-2xl font-serif text-yellow-700">{result.skipped}</p>
                </div>
              </>
            )}
          </div>
          
          {result.errors?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-sm mb-2 text-red-700">Errors ({result.errors.length})</h3>
              <ul className="text-sm text-red-600 bg-red-50 p-4 rounded-lg space-y-1 max-h-40 overflow-y-auto">
                {result.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}
          
          {result.matched_products?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-sm mb-2">Updated Products ({result.matched_products.length})</h3>
              <div className="max-h-40 overflow-y-auto border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-warm-paper sticky top-0">
                    <tr>
                      <th className="p-2 text-left">SKU</th>
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Images Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.matched_products.map((p, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-mono text-xs">{p.sku}</td>
                        <td className="p-2 truncate max-w-xs">{p.name}</td>
                        <td className="p-2">{p.images_added}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          <div className="flex gap-4">
            <Button onClick={resetForm} data-testid="import-another-btn">
              <Upload className="w-4 h-4 mr-2" />
              Import Another File
            </Button>
            <Link to="/admin/products">
              <Button variant="outline">
                <Grid3X3 className="w-4 h-4 mr-2" />
                View Products
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Discount Codes Management
const AdminDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', discount_percentage: 10, max_uses: '', expires_at: '' });
  const [creating, setCreating] = useState(false);

  const fetchDiscounts = async () => {
    try {
      const res = await axios.get(`${API}/admin/discount-codes`);
      setDiscounts(res.data);
    } catch (e) {
      toast.error("Failed to load discount codes");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const createDiscount = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/discount-codes`, {
        code: newCode.code.toUpperCase(),
        discount_percentage: parseInt(newCode.discount_percentage),
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        expires_at: newCode.expires_at || null
      });
      toast.success("Discount code created");
      setShowForm(false);
      setNewCode({ code: '', discount_percentage: 10, max_uses: '', expires_at: '' });
      fetchDiscounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create discount code");
    }
    setCreating(false);
  };

  const deleteDiscount = async (id) => {
    if (!window.confirm("Are you sure you want to delete this discount code?")) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${id}`);
      toast.success("Discount code deleted");
      fetchDiscounts();
    } catch (e) {
      toast.error("Failed to delete discount code");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-warm-muted" />
    </div>
  );

  return (
    <div data-testid="admin-discounts">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl">Discount Codes</h1>
          <p className="text-warm-muted">Create and manage promotional discount codes</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} data-testid="create-discount-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Discount
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-warm p-6 mb-6">
          <h2 className="font-medium text-lg mb-4">Create New Discount Code</h2>
          <form onSubmit={createDiscount} className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">Code</label>
              <Input
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                placeholder="e.g., SUMMER20"
                required
                className="uppercase"
                data-testid="discount-code-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">Discount %</label>
              <Input
                type="number"
                min="1"
                max="100"
                value={newCode.discount_percentage}
                onChange={(e) => setNewCode({ ...newCode, discount_percentage: e.target.value })}
                required
                data-testid="discount-percentage-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">Max Uses (optional)</label>
              <Input
                type="number"
                min="1"
                value={newCode.max_uses}
                onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-warm-muted block mb-2">Expires At (optional)</label>
              <Input
                type="datetime-local"
                value={newCode.expires_at}
                onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
              />
            </div>
            <div className="col-span-2 flex gap-3">
              <Button type="submit" disabled={creating} data-testid="save-discount-btn">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Create Code
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Discounts Table */}
      <div className="bg-white rounded-lg shadow-warm">
        {discounts.length === 0 ? (
          <div className="p-12 text-center">
            <Percent className="w-12 h-12 text-warm-subtle mx-auto mb-4" />
            <p className="text-warm-muted">No discount codes yet</p>
            <Button className="mt-4" onClick={() => setShowForm(true)}>Create Your First Code</Button>
          </div>
        ) : (
          <table className="w-full admin-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Min Order</th>
                <th>Uses</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {discounts.map((discount) => (
                <tr key={discount.id}>
                  <td className="font-mono font-medium">{discount.code}</td>
                  <td>
                    {discount.discount_type === 'percentage' 
                      ? `${discount.discount_value}% off` 
                      : `£${discount.discount_value} off`}
                  </td>
                  <td className="text-warm-muted">
                    {discount.min_order_amount ? `£${discount.min_order_amount}` : '-'}
                  </td>
                  <td>{discount.used_count || 0} / {discount.max_uses || '∞'}</td>
                  <td>
                    <Badge variant={discount.is_active ? "default" : "secondary"}>
                      {discount.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="text-warm-muted">
                    {discount.valid_until ? new Date(discount.valid_until).toLocaleDateString() : 'Never'}
                  </td>
                  <td>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700"
                      onClick={() => deleteDiscount(discount.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Admin Product Tools
const AdminProductTools = () => {
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [scrapeStats, setScrapeStats] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [scrapeLimit, setScrapeLimit] = useState(100);
  const [activeJobId, setActiveJobId] = useState(null);

  // Fetch scrape stats on mount and periodically
  const fetchScrapeStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/products/scrape-stats`);
      setScrapeStats(res.data);
      // If there's an active job, poll its status
      if (res.data.active_jobs?.length > 0) {
        setScraping(true);
        setActiveJobId(res.data.active_jobs[0].job_id);
        setScrapeStatus(res.data.active_jobs[0]);
      }
    } catch (e) {
      console.error("Failed to fetch scrape stats", e);
    }
  };

  useEffect(() => {
    fetchScrapeStats();
    const interval = setInterval(fetchScrapeStats, 5000);
    return () => clearInterval(interval);
  }, []);

  const startScraping = async () => {
    setScraping(true);
    try {
      const res = await axios.post(`${API}/admin/products/scrape-details?limit=${scrapeLimit}&force_all=true`);
      setActiveJobId(res.data.job_id);
      toast.success(`Started scraping ${res.data.batch_size} products`);
      pollScrapeStatus(res.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start scraping");
      setScraping(false);
    }
  };

  const pollScrapeStatus = async (jobId) => {
    try {
      const res = await axios.get(`${API}/admin/products/scrape-job/${jobId}`);
      setScrapeStatus(res.data);
      if (res.data.status === 'running') {
        setTimeout(() => pollScrapeStatus(jobId), 2000);
      } else {
        setScraping(false);
        setActiveJobId(null);
        fetchScrapeStats();
        if (res.data.status === 'completed') {
          toast.success(`Scraping completed! Updated ${res.data.updated} products`);
        } else if (res.data.status === 'failed') {
          toast.error(`Scraping failed: ${res.data.error || 'Unknown error'}`);
        }
      }
    } catch (e) {
      // Job might not exist yet, keep polling
      setTimeout(() => pollScrapeStatus(jobId), 2000);
    }
  };

  const enrichProducts = async () => {
    setEnriching(true);
    try {
      const res = await axios.post(`${API}/admin/products/enrich-missing`);
      toast.success(`Enriched ${res.data.enriched} products with descriptions`);
      fetchScrapeStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to enrich products");
    }
    setEnriching(false);
  };

  const categorizeProducts = async () => {
    setCategorizing(true);
    try {
      const res = await axios.post(`${API}/admin/products/categorize`);
      toast.success(`Categorized ${res.data.updated} products`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to categorize products");
    }
    setCategorizing(false);
  };

  return (
    <div data-testid="admin-product-tools">
      <div className="mb-8">
        <h1 className="font-serif text-3xl">Product Tools</h1>
        <p className="text-warm-muted">Bulk operations and data management for products</p>
      </div>

      {/* Scrape Progress Stats */}
      {scrapeStats && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
          <h2 className="font-medium text-lg mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Product Data Status
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-3xl font-bold text-blue-600">{scrapeStats.total_products}</div>
              <div className="text-sm text-gray-500">Total Products</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-3xl font-bold text-green-600">{scrapeStats.scraped}</div>
              <div className="text-sm text-gray-500">Scraped ({scrapeStats.scrape_percentage}%)</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-3xl font-bold text-orange-600">{scrapeStats.remaining}</div>
              <div className="text-sm text-gray-500">Remaining</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-3xl font-bold text-purple-600">{scrapeStats.with_3plus_images}</div>
              <div className="text-sm text-gray-500">With 3+ Images</div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-2">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Overall Progress</span>
              <span>{scrapeStats.scraped} / {scrapeStats.total_products}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${scrapeStats.scrape_percentage}%` }}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-sm mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>With Description: {scrapeStats.with_description}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span>With SKU: {scrapeStats.with_sku}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span>With Dimensions: {scrapeStats.with_dimensions}</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* Scrape Product Details */}
        <div className="bg-white rounded-lg shadow-warm p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="font-medium text-lg">Scrape Product Details</h2>
              <p className="text-sm text-warm-muted">Fetch SKU, dimensions, description, images from source</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm text-warm-muted">Batch size:</label>
            <select
              value={scrapeLimit}
              onChange={(e) => setScrapeLimit(Number(e.target.value))}
              className="border rounded-lg px-3 py-2"
              disabled={scraping}
            >
              <option value={50}>50 products</option>
              <option value={100}>100 products</option>
              <option value={200}>200 products</option>
              <option value={500}>500 products (slow)</option>
            </select>
          </div>
          
          {/* Active Job Status */}
          {scraping && scrapeStatus && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-blue-700 font-medium">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Scraping in Progress</span>
                </div>
                <span className="text-sm text-blue-600">
                  {scrapeStatus.completed || 0} / {scrapeStatus.total || scrapeLimit}
                </span>
              </div>
              
              <div className="w-full bg-blue-200 rounded-full h-2 mb-3">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${((scrapeStatus.completed || 0) / (scrapeStatus.total || scrapeLimit)) * 100}%` }}
                />
              </div>
              
              {scrapeStatus.current_product && (
                <div className="text-sm text-blue-600 truncate">
                  Current: {scrapeStatus.current_product}
                </div>
              )}
              
              <div className="flex gap-4 mt-2 text-xs text-blue-600">
                <span>Updated: {scrapeStatus.updated || 0}</span>
                <span>Failed: {scrapeStatus.failed || 0}</span>
              </div>
            </div>
          )}
          
          <Button 
            onClick={startScraping} 
            disabled={scraping || (scrapeStats && scrapeStats.remaining === 0)} 
            data-testid="scrape-btn"
            className="w-full"
          >
            {scraping ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Scraping...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                {scrapeStats && scrapeStats.remaining === 0 ? 'All Products Scraped' : 'Start Scraping'}
              </>
            )}
          </Button>
          
          {scrapeStats && scrapeStats.remaining > 0 && !scraping && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              {scrapeStats.remaining} products remaining to scrape
            </p>
          )}
        </div>

        {/* Enrich Products */}
        <div className="bg-white rounded-lg shadow-warm p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="font-medium text-lg">Enrich Descriptions</h2>
              <p className="text-sm text-warm-muted">Generate AI descriptions for products missing them</p>
            </div>
          </div>
          <Button onClick={enrichProducts} disabled={enriching} data-testid="enrich-btn">
            {enriching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Enrich Products
          </Button>
        </div>

        {/* Categorize Products */}
        <div className="bg-white rounded-lg shadow-warm p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Layers className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="font-medium text-lg">Auto-Categorize</h2>
              <p className="text-sm text-warm-muted">Automatically assign categories based on product names</p>
            </div>
          </div>
          <Button onClick={categorizeProducts} disabled={categorizing} data-testid="categorize-btn">
            {categorizing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />}
            Categorize All
          </Button>
        </div>

        {/* Export Products */}
        <div className="bg-white rounded-lg shadow-warm p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="font-medium text-lg">Export Products</h2>
              <p className="text-sm text-warm-muted">Download all products as CSV file</p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={() => window.open(`${API}/export/csv`, '_blank')}
            data-testid="export-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Download CSV
          </Button>
        </div>
      </div>
    </div>
  );
};

// Admin Navigation Management
const AdminNavigation = () => {
  const [navigation, setNavigation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/navigation`).then(res => {
      setNavigation(res.data.items || []);
      setLoading(false);
    });
  }, []);

  const toggleEnabled = (id) => {
    setNavigation(navigation.map(item => 
      item.id === id ? { ...item, enabled: !item.enabled } : item
    ));
  };

  const saveNavigation = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/navigation`, navigation);
      toast.success("Navigation saved");
    } catch (e) {
      toast.error("Failed to save navigation");
    }
    setSaving(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-warm-muted" />
    </div>
  );

  return (
    <div data-testid="admin-navigation">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl">Navigation</h1>
          <p className="text-warm-muted">Manage your store's navigation menu</p>
        </div>
        <Button onClick={saveNavigation} disabled={saving} data-testid="save-nav-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-warm">
        <table className="w-full admin-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Label</th>
              <th>Type</th>
              <th>Enabled</th>
            </tr>
          </thead>
          <tbody>
            {navigation.sort((a, b) => a.order - b.order).map((item) => (
              <tr key={item.id}>
                <td className="text-warm-muted">{item.order}</td>
                <td className="font-medium">{item.label}</td>
                <td>
                  <Badge variant="secondary">{item.type}</Badge>
                </td>
                <td>
                  <button
                    onClick={() => toggleEnabled(item.id)}
                    className={`w-10 h-6 rounded-full transition-colors ${item.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`block w-4 h-4 bg-white rounded-full transition-transform ${item.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Inventory Sync Settings Component
const InventorySyncSettings = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleHour, setScheduleHour] = useState(1);
  const [scheduleMinute, setScheduleMinute] = useState(0);
  const [savingSchedule, setSavingSchedule] = useState(false);
  
  // Notification settings state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notifyAdmin, setNotifyAdmin] = useState(true);
  const [customEmails, setCustomEmails] = useState('');
  const [stockThreshold, setStockThreshold] = useState(5);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [manualQuantityFile, setManualQuantityFile] = useState(null);
  const [uploadingQuantityCsv, setUploadingQuantityCsv] = useState(false);
  const [manualUploadResult, setManualUploadResult] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchLogs();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/inventory-sync/status`);
      setSyncStatus(res.data);
      setScheduleEnabled(res.data.enabled);
      if (res.data.schedule_time) {
        const [h, m] = res.data.schedule_time.split(':').map(Number);
        setScheduleHour(h);
        setScheduleMinute(m);
      }
      // Load notification settings
      if (res.data.notifications) {
        setNotificationsEnabled(res.data.notifications.enabled || false);
        setNotifyAdmin(res.data.notifications.notify_admin !== false);
        setCustomEmails((res.data.notifications.custom_emails || []).join(', '));
        setStockThreshold(res.data.notifications.threshold || 5);
      }
    } catch (e) {
      console.error('Failed to fetch inventory sync status');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/inventory-sync/logs?limit=10`);
      setLogs(res.data.logs || []);
    } catch (e) {
      console.error('Failed to fetch sync logs');
    }
  };

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    setSavingCredentials(true);
    try {
      await axios.post(`${API}/admin/inventory-sync/credentials`, credentials);
      toast.success('Credentials saved successfully');
      setShowCredentialsModal(false);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await axios.post(`${API}/admin/inventory-sync/schedule`, {
        enabled: scheduleEnabled,
        hour: scheduleHour,
        minute: scheduleMinute
      });
      toast.success(`Schedule ${scheduleEnabled ? 'enabled' : 'disabled'}`);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleRunSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/inventory-sync/run`);
      toast.success('Sync job started. Check logs for progress.');
      // Poll for updates
      const pollInterval = setInterval(async () => {
        await fetchStatus();
        await fetchLogs();
        if (!syncStatus?.is_running) {
          clearInterval(pollInterval);
          setSyncing(false);
        }
      }, 3000);
      // Stop polling after 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setSyncing(false);
      }, 120000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start sync');
      setSyncing(false);
    }
  };

  const handleManualQuantityCsvUpload = async () => {
    if (!manualQuantityFile) {
      toast.error('Please choose a CSV file first');
      return;
    }

    setUploadingQuantityCsv(true);
    setManualUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', manualQuantityFile);

      const res = await axios.post(`${API}/admin/inventory-sync/upload-quantity-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setManualUploadResult(res.data);
      toast.success(res.data.message || 'Manual quantity CSV uploaded');
      fetchStatus();
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload quantity CSV');
    } finally {
      setUploadingQuantityCsv(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      // Parse custom emails
      const emailList = customEmails
        .split(',')
        .map(e => e.trim())
        .filter(e => e && e.includes('@'));
      
      await axios.post(`${API}/admin/inventory-sync/notifications`, {
        enabled: notificationsEnabled,
        threshold: stockThreshold,
        notify_admin: notifyAdmin,
        custom_emails: emailList,
        send_daily_summary: true
      });
      toast.success('Notification settings saved');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save notification settings');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSendTestNotification = async () => {
    setSendingTestEmail(true);
    try {
      await axios.post(`${API}/admin/inventory-sync/test-notification`);
      toast.success('Test notification sent! Check your inbox.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-testid="inventory-sync-settings">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Inventory Sync</h2>
              <p className="text-sm text-gray-500">Automated stock updates from supplier</p>
            </div>
          </div>
          {syncStatus?.credentials_configured ? (
            <Badge className="bg-green-100 text-green-700 border-green-200">Configured</Badge>
          ) : (
            <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Setup Required</Badge>
          )}
        </div>
        
        <div className="p-6 space-y-6">
          {/* Credentials Section */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500 block mb-1">Supplier</label>
              <span className="bg-gray-100 px-3 py-2 rounded text-sm block">Furniture To Go</span>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500 block mb-1">Credentials</label>
              <div className="flex items-center gap-2">
                {syncStatus?.credentials_configured ? (
                  <>
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Configured
                    </span>
                    <Button variant="outline" size="sm" onClick={() => setShowCredentialsModal(true)}>
                      Update
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setShowCredentialsModal(true)}>
                    <Key className="w-4 h-4 mr-2" /> Add Credentials
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Schedule Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-gray-900">Automatic Sync Schedule</h3>
                <p className="text-sm text-gray-500">Run inventory sync daily at a specified time</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-600">{scheduleEnabled ? 'Enabled' : 'Disabled'}</span>
                <button
                  type="button"
                  onClick={() => setScheduleEnabled(!scheduleEnabled)}
                  className={`w-12 h-6 rounded-full transition-colors ${scheduleEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${scheduleEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </label>
            </div>
            
            <div className="flex items-end gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Time (24h)</label>
                <div className="flex items-center gap-2">
                  <select 
                    value={scheduleHour}
                    onChange={(e) => setScheduleHour(Number(e.target.value))}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    {Array.from({length: 24}, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                    ))}
                  </select>
                  <span className="text-gray-500">:</span>
                  <select 
                    value={scheduleMinute}
                    onChange={(e) => setScheduleMinute(Number(e.target.value))}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    {[0, 15, 30, 45].map(m => (
                      <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <Button onClick={handleSaveSchedule} disabled={savingSchedule} variant="outline">
                {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
                Save Schedule
              </Button>
              {syncStatus?.next_run && (
                <span className="text-sm text-gray-500">Next run: {formatDate(syncStatus.next_run)}</span>
              )}
            </div>
          </div>

          {/* Manual Sync */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">Manual Sync</h3>
                <p className="text-sm text-gray-500">Run inventory sync immediately</p>
              </div>
              <Button 
                onClick={handleRunSync} 
                disabled={!syncStatus?.credentials_configured || syncing || syncStatus?.is_running}
              >
                {syncing || syncStatus?.is_running ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Run Sync Now
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Manual Quantity CSV Upload */}
          <div className="border-t pt-6" data-testid="inventory-sync-manual-csv-section">
            <div className="flex flex-col xl:flex-row xl:items-end gap-4 justify-between">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">Manual Quantity CSV Upload</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Update quantities using CSV columns like <code>SKU</code> or <code>ItemNumber</code> with quantity fields (e.g., <code>Qty On Hand</code>, <code>QuantityInStock</code>).
                </p>
                <Input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setManualQuantityFile(e.target.files?.[0] || null)}
                  data-testid="inventory-sync-manual-csv-input"
                />
              </div>
              <Button
                onClick={handleManualQuantityCsvUpload}
                disabled={uploadingQuantityCsv || !manualQuantityFile}
                data-testid="inventory-sync-manual-csv-upload-button"
              >
                {uploadingQuantityCsv ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Quantity CSV
                  </>
                )}
              </Button>
            </div>

            {manualUploadResult && (
              <div className="mt-4 p-4 rounded-lg bg-blue-50 border border-blue-200" data-testid="inventory-sync-manual-csv-result">
                <p className="text-sm text-blue-900 font-medium mb-2">Manual Upload Result</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div data-testid="inventory-sync-manual-csv-total-rows">Rows: <strong>{manualUploadResult.total_csv_rows || 0}</strong></div>
                  <div data-testid="inventory-sync-manual-csv-updated">Updated: <strong>{manualUploadResult.updated_count || 0}</strong></div>
                  <div data-testid="inventory-sync-manual-csv-skipped">Skipped: <strong>{manualUploadResult.skipped_count || 0}</strong></div>
                  <div data-testid="inventory-sync-manual-csv-not-found">Not Found: <strong>{(manualUploadResult.not_found_skus || []).length}</strong></div>
                </div>
              </div>
            )}
          </div>

          {/* Last Sync Status */}
          {syncStatus?.last_sync && (
            <div className={`p-4 rounded-lg ${syncStatus.last_sync.status === 'success' ? 'bg-green-50' : syncStatus.last_sync.status === 'failed' ? 'bg-red-50' : 'bg-blue-50'}`}>
              <div className="flex items-start gap-3">
                {syncStatus.last_sync.status === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                ) : syncStatus.last_sync.status === 'failed' ? (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                ) : (
                  <Loader2 className="w-5 h-5 text-blue-600 mt-0.5 animate-spin" />
                )}
                <div className="text-sm">
                  <p className={`font-medium ${syncStatus.last_sync.status === 'success' ? 'text-green-800' : syncStatus.last_sync.status === 'failed' ? 'text-red-800' : 'text-blue-800'}`}>
                    Last sync: {syncStatus.last_sync.status === 'success' ? 'Successful' : syncStatus.last_sync.status === 'failed' ? 'Failed' : 'Running'}
                  </p>
                  <p className="text-gray-600 mt-1">
                    {formatDate(syncStatus.last_sync.time)} • {syncStatus.last_sync.updated_count} products updated
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sync Logs */}
          {logs.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="font-medium text-gray-900 mb-4">Recent Sync History</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Time</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Triggered By</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Products Updated</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {logs.map((log, i) => (
                      <tr key={log.job_id || i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">{formatDate(log.start_time)}</td>
                        <td className="px-4 py-3 capitalize text-gray-600">{log.triggered_by || 'manual'}</td>
                        <td className="px-4 py-3">
                          <Badge className={
                            log.status === 'success' ? 'bg-green-100 text-green-700' :
                            log.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }>
                            {log.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{log.updated_count || 0}</td>
                        <td className="px-4 py-3 text-gray-500 truncate max-w-xs">{log.message || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Email Notifications Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Email Notifications</h3>
                  <p className="text-sm text-gray-500">Get notified about sync failures and low stock</p>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-600">{notificationsEnabled ? 'Enabled' : 'Disabled'}</span>
                <button
                  type="button"
                  onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                  className={`w-12 h-6 rounded-full transition-colors ${notificationsEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${notificationsEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </label>
            </div>

            <div className="space-y-4 pl-11">
              {/* Low Stock Threshold */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 block mb-1">Low Stock Threshold</label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={stockThreshold}
                      onChange={(e) => setStockThreshold(parseInt(e.target.value) || 5)}
                      className="w-24"
                    />
                    <span className="text-sm text-gray-500">units</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Products below this quantity will be flagged as low stock</p>
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Notification Recipients</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifyAdmin}
                      onChange={(e) => setNotifyAdmin(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Send to admin email</span>
                  </label>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Custom email addresses (comma-separated)</label>
                    <Input
                      type="text"
                      value={customEmails}
                      onChange={(e) => setCustomEmails(e.target.value)}
                      placeholder="manager@example.com, warehouse@example.com"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Notification Types Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 mb-2">What you'll receive:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>Immediate alert when sync fails</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    <span>Daily summary of low stock and out of stock products</span>
                  </li>
                </ul>
              </div>

              {/* Save and Test Buttons */}
              <div className="flex items-center gap-3 pt-2">
                <Button onClick={handleSaveNotifications} disabled={savingNotifications}>
                  {savingNotifications ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Notification Settings
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleSendTestNotification}
                  disabled={!notificationsEnabled || sendingTestEmail}
                >
                  {sendingTestEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Test Email
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Credentials Modal */}
      {showCredentialsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Supplier Credentials</h3>
              <button onClick={() => setShowCredentialsModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveCredentials}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Username / Email</label>
                  <Input
                    type="text"
                    value={credentials.username}
                    onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                    placeholder="supplier@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Password</label>
                  <Input
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                    placeholder="••••••••"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500">
                  These credentials are used to log into furniture-to-go.co.uk to download the stocklist CSV.
                </p>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button type="button" variant="outline" onClick={() => setShowCredentialsModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={savingCredentials}>
                  {savingCredentials ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Credentials
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// Admin Settings
const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`);
      setSettings(res.data);
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      await axios.post(`${API}/admin/test-email`);
      toast.success("Test email sent! Check your inbox.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500">Manage your store integrations and settings</p>
      </div>

      <div className="grid gap-6">
        {/* Stripe Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#635bff] rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Stripe Payments</h2>
                <p className="text-sm text-gray-500">Payment processing configuration</p>
              </div>
            </div>
            {settings?.stripe?.configured ? (
              <Badge className="bg-green-100 text-green-700 border-green-200">
                {settings.stripe.mode === 'test' ? 'Test Mode' : 'Live Mode'}
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-700 border-red-200">Not Configured</Badge>
            )}
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">API Key</label>
                <div className="flex items-center gap-2">
                  <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono flex-1">
                    {settings?.stripe?.key_preview || 'Not set'}
                  </code>
                  {settings?.stripe?.configured && (
                    <span className="text-green-600">
                      <CheckCircle className="w-5 h-5" />
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Webhook URL</label>
                <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono block">
                  {settings?.stripe?.webhook_url || '/api/webhook/stripe'}
                </code>
              </div>
            </div>
            {settings?.stripe?.configured && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Stripe is {settings.stripe.mode === 'test' ? 'in test mode' : 'live'}.</p>
                    <p className="mt-1">
                      {settings.stripe.mode === 'test' 
                        ? 'Use test card numbers like 4242 4242 4242 4242 for testing.'
                        : 'Real payments are being processed.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Email Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#000000] rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Email Notifications</h2>
                <p className="text-sm text-gray-500">Order confirmation and transactional emails</p>
              </div>
            </div>
            {settings?.email?.configured ? (
              <Badge className="bg-green-100 text-green-700 border-green-200">Connected</Badge>
            ) : (
              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Not Configured</Badge>
            )}
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Provider</label>
                <div className="flex items-center gap-2">
                  <span className="bg-gray-100 px-3 py-2 rounded text-sm flex-1">
                    {settings?.email?.provider || 'Resend'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Sender Email</label>
                <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono block">
                  {settings?.email?.sender_email || 'Not configured'}
                </code>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">API Key</label>
                <div className="flex items-center gap-2">
                  <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono flex-1">
                    {settings?.email?.key_preview || 'Not set'}
                  </code>
                  {settings?.email?.configured && (
                    <span className="text-green-600">
                      <CheckCircle className="w-5 h-5" />
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Test Email</label>
                <Button 
                  onClick={sendTestEmail} 
                  disabled={!settings?.email?.configured || testingEmail}
                  variant="outline"
                  className="w-full"
                >
                  {testingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Test Email
                    </>
                  )}
                </Button>
              </div>
            </div>
            {settings?.email?.configured && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Email notifications are enabled.</p>
                    <p className="mt-1">Order confirmation emails will be sent automatically when payments succeed.</p>
                  </div>
                </div>
              </div>
            )}
            {!settings?.email?.configured && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Email notifications are disabled.</p>
                    <p className="mt-1">Add RESEND_API_KEY to your environment to enable order confirmation emails.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Feature Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">Feature Status</h2>
            <p className="text-sm text-gray-500">Overview of enabled features</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`p-4 rounded-lg ${settings?.features?.stripe_checkout ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {settings?.features?.stripe_checkout ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="font-medium text-sm">Stripe Checkout</span>
                </div>
                <p className="text-xs text-gray-500">Payment processing</p>
              </div>
              <div className={`p-4 rounded-lg ${settings?.features?.order_confirmation_emails ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {settings?.features?.order_confirmation_emails ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="font-medium text-sm">Email Receipts</span>
                </div>
                <p className="text-xs text-gray-500">Order confirmations</p>
              </div>
              <div className={`p-4 rounded-lg ${settings?.features?.discount_codes ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {settings?.features?.discount_codes ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="font-medium text-sm">Discount Codes</span>
                </div>
                <p className="text-xs text-gray-500">Promotions & coupons</p>
              </div>
              <div className={`p-4 rounded-lg ${settings?.features?.ai_chatbot ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {settings?.features?.ai_chatbot ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="font-medium text-sm">AI Chatbot</span>
                </div>
                <p className="text-xs text-gray-500">Customer support</p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">Payment Statistics</h2>
            <p className="text-sm text-gray-500">Overview of payment transactions</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{settings?.stats?.total_orders || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Total Orders</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{settings?.stats?.paid_orders || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Paid Orders</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">£{settings?.stats?.total_revenue?.toLocaleString() || '0'}</p>
                <p className="text-sm text-gray-500 mt-1">Total Revenue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Inventory Sync Section */}
        <InventorySyncSettings />

        {/* Store Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">Store Information</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-2">Store Name</label>
                <Input value="Voco Living" disabled />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-2">Currency</label>
                <Input value="GBP (£)" disabled />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">Quick Links</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-3 gap-4">
              <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <Home className="w-5 h-5 text-gray-400" />
                <span>View Store</span>
              </a>
              <Link to="/admin/products" className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <Grid3X3 className="w-5 h-5 text-gray-400" />
                <span>Products</span>
              </Link>
              <Link to="/admin/orders" className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <Package className="w-5 h-5 text-gray-400" />
                <span>Orders</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// Password Reset Page
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Password reset link sent!");
    } catch (err) {
      toast.error("Failed to send reset link");
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
        <h1 className="font-serif text-3xl mb-4">Check Your Email</h1>
        <p className="text-warm-muted mb-6">We've sent a password reset link to {email}. Please check your inbox.</p>
        <Link to="/login" className="text-accent-terracotta hover:underline">Back to Sign In</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="forgot-password-page">
      <h1 className="font-serif text-3xl text-center mb-2">Forgot Password?</h1>
      <p className="text-center text-warm-muted mb-8">Enter your email and we'll send you a reset link.</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="reset-email-input"
        />
        <Button type="submit" className="w-full btn-primary" disabled={loading}>
          {loading ? "Sending..." : "Send Reset Link"}
        </Button>
      </form>
      <p className="text-center mt-6">
        <Link to="/login" className="text-accent-terracotta hover:underline">Back to Sign In</Link>
      </p>
    </div>
  );
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset successfully!");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
        <h1 className="font-serif text-3xl mb-4">Password Reset!</h1>
        <p className="text-warm-muted">Redirecting to login...</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="reset-password-page">
      <h1 className="font-serif text-3xl text-center mb-2">Reset Password</h1>
      <p className="text-center text-warm-muted mb-8">Enter your new password below.</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          type="password"
          placeholder="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <Input
          type="password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        <Button type="submit" className="w-full btn-primary" disabled={loading}>
          {loading ? "Resetting..." : "Reset Password"}
        </Button>
      </form>
    </div>
  );
};

// AI Chatbot Widget
const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: userMessage });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble responding. Please try again or email support@vocoliving.com." }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-6 z-50 w-14 h-14 bg-warm-dark text-white rounded-full shadow-lg hover:bg-warm-dark/90 transition-colors flex items-center justify-center"
        data-testid="chat-widget-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md h-[500px] flex flex-col p-0">
          <DialogHeader className="px-4 py-3 border-b bg-warm-dark text-white rounded-t-lg">
            <DialogTitle className="text-lg flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Voco Living Support
            </DialogTitle>
            <DialogDescription className="text-white/70 text-sm">
              How can we help you today?
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 p-4">
            {messages.length === 0 && (
              <div className="text-center py-8 text-warm-muted">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Hi! I'm your furniture assistant.</p>
                <p className="text-sm mt-2">Ask me about our products, delivery, or returns.</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`mb-4 flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                  msg.role === "user" 
                    ? "bg-warm-dark text-white rounded-br-md" 
                    : "bg-warm-subtle text-warm-dark rounded-bl-md"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start mb-4">
                <div className="bg-warm-subtle text-warm-dark px-4 py-2 rounded-2xl rounded-bl-md">
                  <span className="animate-pulse">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </ScrollArea>

          <form onSubmit={sendMessage} className="p-4 border-t flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              disabled={loading}
              className="flex-1"
              data-testid="chat-input"
            />
            <Button type="submit" disabled={loading || !input.trim()} className="btn-primary" data-testid="chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Product Comparison Bar
const ComparisonBar = () => {
  const { compareItems, removeFromCompare, clearComparison } = useComparison();
  const navigate = useNavigate();

  if (compareItems.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-warm shadow-lg z-40 py-3 px-6" data-testid="comparison-bar">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-warm-dark">Compare ({compareItems.length}/4):</span>
          <div className="flex gap-2">
            {compareItems.map((product) => (
              <div key={product.id} className="relative group">
                <div className="w-12 h-12 bg-warm-subtle rounded overflow-hidden">
                  {product.images?.[0] && (
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                  )}
                </div>
                <button
                  onClick={() => removeFromCompare(product.id)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" onClick={clearComparison}>
            Clear
          </Button>
          <Button 
            size="sm" 
            className="btn-primary"
            disabled={compareItems.length < 2}
            onClick={() => navigate("/compare")}
          >
            Compare Now
          </Button>
        </div>
      </div>
    </div>
  );
};

// Product Comparison Page
const ComparisonPage = () => {
  const { compareItems, removeFromCompare, clearComparison } = useComparison();
  const { addToCart } = useCart();

  if (compareItems.length < 2) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 text-center" data-testid="comparison-page">
        <GitCompare className="w-16 h-16 text-warm-subtle mx-auto mb-6" />
        <h1 className="font-serif text-3xl mb-4">Compare Products</h1>
        <p className="text-warm-muted mb-8">Add at least 2 products to compare. You can add products from the shop page.</p>
        <Link to="/shop">
          <Button className="btn-primary">Browse Products</Button>
        </Link>
      </div>
    );
  }

  const specs = [
    { key: "price", label: "Price" },
    { key: "rrp_price", label: "RRP" },
    { key: "collection_name", label: "Collection" },
    { key: "category", label: "Category" },
    { key: "product_type", label: "Type" },
    { key: "sku", label: "SKU" },
    { key: "size_cm", label: "Dimensions" },
    { key: "assembled_weight_kg", label: "Weight" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="comparison-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl">Compare Products</h1>
        <Button variant="outline" onClick={clearComparison}>Clear All</Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[600px]">
          <thead>
            <tr>
              <th className="p-4 text-left w-32"></th>
              {compareItems.map((product) => (
                <th key={product.id} className="p-4 text-center border-l border-warm">
                  <div className="relative">
                    <button
                      onClick={() => removeFromCompare(product.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-sm"
                    >
                      ×
                    </button>
                    <Link to={`/product/${product.id}`}>
                      <div className="w-40 h-40 mx-auto bg-warm-subtle rounded-lg overflow-hidden mb-4">
                        {product.images?.[0] && (
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <h3 className="font-serif text-lg line-clamp-2 hover:text-accent-terracotta">{product.name}</h3>
                    </Link>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {specs.map((spec) => (
              <tr key={spec.key} className="border-t border-warm">
                <td className="p-4 font-medium text-warm-muted">{spec.label}</td>
                {compareItems.map((product) => (
                  <td key={product.id} className="p-4 text-center border-l border-warm">
                    {product[spec.key] || "—"}
                    {spec.key === "assembled_weight_kg" && product[spec.key] ? " kg" : ""}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-warm bg-warm-subtle">
              <td className="p-4"></td>
              {compareItems.map((product) => (
                <td key={product.id} className="p-4 text-center border-l border-warm">
                  <Button onClick={() => addToCart(product.id)} className="btn-primary w-full">
                    Add to Cart
                  </Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Footer - Furniture Village Style
const Footer = () => (
  <footer className="bg-[#1a1a1a] text-white">
    {/* Newsletter Section */}
    <div className="bg-[#003366] py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold mb-1">Get £25 off your first order</h3>
            <p className="text-white/80 text-sm">Sign up to our newsletter for exclusive offers</p>
          </div>
          <div className="flex w-full md:w-auto gap-2">
            <Input 
              placeholder="Enter your email" 
              className="bg-white text-gray-900 border-0 w-full md:w-64"
            />
            <Button className="bg-white text-[#003366] hover:bg-gray-100 font-bold px-6">
              Sign Up
            </Button>
          </div>
        </div>
      </div>
    </div>

    {/* Main Footer */}
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Logo & About */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-4">
              <span className="text-2xl font-bold">VOCO</span>
              <span className="text-2xl font-light ml-1">Living</span>
            </div>
            <p className="text-gray-400 text-sm">The UK's destination for quality furniture at great prices.</p>
          </div>

          {/* Shop */}
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wide">Shop</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/living" className="hover:text-white transition-colors">Living Room</Link></li>
              <li><Link to="/dining" className="hover:text-white transition-colors">Dining Room</Link></li>
              <li><Link to="/bedroom" className="hover:text-white transition-colors">Bedroom</Link></li>
              <li><Link to="/sale" className="hover:text-white transition-colors text-[#c41e3a]">Sale</Link></li>
              <li><Link to="/collections" className="hover:text-white transition-colors">Collections</Link></li>
            </ul>
          </div>

          {/* Help */}
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wide">Help</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/customer-service/delivery" className="hover:text-white transition-colors">Delivery Information</Link></li>
              <li><Link to="/customer-service/returns" className="hover:text-white transition-colors">Returns & Refunds</Link></li>
              <li><Link to="/customer-service/faq" className="hover:text-white transition-colors">FAQs</Link></li>
              <li><Link to="/customer-service/contact-us" className="hover:text-white transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* Account */}
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wide">Account</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
              <li><Link to="/register" className="hover:text-white transition-colors">Create Account</Link></li>
              <li><Link to="/account" className="hover:text-white transition-colors">My Orders</Link></li>
              <li><Link to="/wishlist" className="hover:text-white transition-colors">Wishlist</Link></li>
            </ul>
          </div>

          {/* About */}
          <div>
            <h4 className="font-bold mb-4 text-sm uppercase tracking-wide">About Us</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/customer-service" className="hover:text-white transition-colors">About Voco Living</Link></li>
              <li><Link to="/customer-service/warranty" className="hover:text-white transition-colors">1 Year Guarantee</Link></li>
              <li><Link to="/customer-service/finance" className="hover:text-white transition-colors">Price Match Guaranteed</Link></li>
            </ul>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-8 mt-12 pt-8 border-t border-gray-800">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <ShieldCheck className="w-5 h-5" />
            <span>1 Year Guarantee</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Tag className="w-5 h-5" />
            <span>Price Match Guaranteed</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Truck className="w-5 h-5" />
            <span>Free Delivery to UK Mainland</span>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom Bar */}
    <div className="border-t border-gray-800 py-6">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between text-gray-500 text-sm gap-4">
          <p>© 2025 Voco Living. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/customer-service/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/customer-service/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/customer-service/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </div>
  </footer>
);

// Main App
function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <ComparisonProvider>
            <RecentlyViewedProvider>
              <Toaster position="top-right" richColors />
              <SitewideSEOManager />
              <Routes>
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
                <Route path="/admin/orders" element={<AdminLayout><AdminOrders /></AdminLayout>} />
                <Route path="/admin/products" element={<AdminLayout><AdminProducts /></AdminLayout>} />
                <Route path="/admin/import" element={<AdminLayout><AdminImportProducts /></AdminLayout>} />
                <Route path="/admin/import-manager" element={<AdminLayout><AdminImportManagerPage /></AdminLayout>} />
                <Route path="/admin/customers" element={<AdminLayout><AdminCustomers /></AdminLayout>} />
                <Route path="/admin/discounts" element={<AdminLayout><AdminDiscounts /></AdminLayout>} />
                <Route path="/admin/seo-audit" element={<AdminLayout><AdminSEOAuditPage /></AdminLayout>} />
                <Route path="/admin/profitability" element={<AdminLayout><ProfitabilityManager /></AdminLayout>} />
                <Route path="/admin/data-manager" element={<AdminLayout><ProductDataManager /></AdminLayout>} />
                <Route path="/admin/tad-import" element={<AdminLayout><TadImportManager /></AdminLayout>} />
                <Route path="/admin/database" element={<AdminLayout><DatabaseMigration /></AdminLayout>} />
                <Route path="/admin/product-tools" element={<AdminLayout><AdminProductTools /></AdminLayout>} />
                <Route path="/admin/navigation" element={<AdminLayout><AdminNavigation /></AdminLayout>} />
                <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
                
                {/* Customer Routes */}
                <Route path="*" element={
                  <>
                    <Header />
                    <main className="min-h-screen pb-16">
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/shop" element={<ShopPage />} />
                        <Route path="/product/:id" element={<ProductDetailPage />} />
                        <Route path="/collections" element={<CollectionsPage />} />
                        <Route path="/collection/:collectionName" element={<CollectionDetailPage />} />
                        <Route path="/bedroom" element={<BedroomPage />} />
                        <Route path="/living" element={<LivingRoomPage />} />
                        <Route path="/dining" element={<DiningRoomPage />} />
                        <Route path="/kids" element={<KidsRoomPage />} />
                        <Route path="/home-office" element={<HomeOfficePage />} />
                        <Route path="/packages" element={<PackagesPage />} />
                        <Route path="/category/:categoryId" element={<CategoryPage />} />
                        <Route path="/sale" element={<SalePage />} />
                        <Route path="/compare" element={<ComparisonPage />} />
                        <Route path="/customer-service" element={<CustomerServicePage />} />
                        <Route path="/customer-service/:pageId" element={<CustomerServicePage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/account" element={<AccountPage />} />
                        <Route path="/order/:orderId/track" element={<OrderTrackingPage />} />
                        <Route path="/wishlist" element={<WishlistPage />} />
                        <Route path="/checkout" element={<CheckoutPage />} />
                        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                        <Route path="/checkout/cancel" element={<Navigate to="/checkout" />} />
                      </Routes>
                    </main>
                    <ComparisonBar />
                    <ChatbotWidget />
                    <Footer />
                  </>
                } />
              </Routes>
            </RecentlyViewedProvider>
          </ComparisonProvider>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
